// NC-ported manager-view preview component (3,987 LOC). The chart layers
// graft extra computed fields onto BE shapes (key/fill/fullName on the
// funnel; gamification sub-trees on the weekly recap), so dashboardData
// is read through a permissive Record<string, any> alias (LooseRow). That
// gives every downstream callback a typed parameter without forcing every
// nested shape to be enumerated. Tightening to concrete DTOs is a future
// epic — when it lands, narrow LooseRow and remove the `as unknown as
// FilterableEntity[]` casts that thread BE rows into the filter drawer.
'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users,
  UserCheck,
  UserCog,
  TrendingUp,
  TrendingDown,
  Trophy,
  Award,
  Target,
  ShieldAlert,
  ExternalLink,
  AlertTriangle,
  Megaphone,
  ShieldCheck,
  RefreshCw,
  Clock,
  FileText,
  FileType,
  FileSpreadsheet,
  Download,
  Camera,
  Network,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  PartyPopper,
  X,
  Frown,
  GraduationCap,
  CheckCircle2,
  XCircle,
  ArrowUp,
  Bell,
  Filter,
  Search,
  ArrowUpDown,
  Mail,
  Eye,
  MousePointer,
  Shield,
  BookOpen,
  Circle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UnifiedDataTable } from '@/components/ui/unified-data-table';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AdvancedFilterDrawer } from '@/app/settings/portal/_components/manager/advanced-filter-drawer';
import type { FilterableEntity, GamificationHistoryEvent, WeeklyRecap } from '@/lib/teams-portal/types';
import {
  EMPTY_ANNOUNCEMENT_KPIS,
  EMPTY_PHISHING_KPIS,
  EMPTY_TRAINING_KPIS,
  teamKpiQuery,
  type TeamKpiTotals,
} from '@/lib/teams-portal/team-kpis';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { HierarchyTree } from '@/app/settings/portal/_components/manager/hierarchy-tree';
import { fetchPortalApi } from '@/lib/api/portal-fetch';
import { portalAdminFetch } from '@/lib/portal/api';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusPanel } from '@/components/status/status-panel';
import { UserText } from '@/components/ui/user-text';
import { normalizeTimeZone } from '@/lib/time/zone';
import { getMonogram } from '@/lib/utils/get-monogram';
import {
  isWeeklyRecapDismissed,
  dismissWeeklyRecap as persistWeeklyRecapDismissal,
} from '@/lib/portal/weekly-recap-dismissal';
import { directionFor, useTranslation } from '@/lib/i18n';
import {
  DATE,
  DATE_TIME,
  formatLearnerDate,
  richText,
} from '@/components/user-portal/learner-i18n';
import { BadgeIcon } from '@/components/gamification/badge-icon';
import { earnedRung, sortLadder } from '@/lib/gamification/level-ladder';
import type { Level } from '@/lib/types/gamification';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * A risk level the API sends in English ('High' | 'Medium' | 'Low') in the
 * reader's language; anything else is shown as sent.
 */
const riskLevelText = (t: Translate, level: string): string => {
  switch (level) {
    case 'High':
      return t('learner.team.risk.high');
    case 'Medium':
      return t('learner.team.risk.medium');
    case 'Low':
      return t('learner.team.risk.low');
    default:
      return level;
  }
};

// Types
interface TeamMember {
  id: string;
  name: string;
  email: string;
  department: string;
  riskLevel: 'High' | 'Medium' | 'Low';
  totalPoints: number;
  badgeCount: number;
  badges: string[];
  rank: number;
  trend: number;
  lastActivity: string;
  isDirectReport: boolean;
  isManager: boolean;
  reportsTo?: string;
  timesCompromised?: number;
  trainingCompletion?: number;
  reportRate?: number;
  overdueRate?: number;
  avatar?: string;
}



/**
 * The API's name for each dashboard section it may report in
 * `unavailableSections` (audit T18), as the catalogue key of the words a
 * manager reads (messages/learner, `learner.team.sections.*`).
 */
const SECTION_LABELS: Record<string, string> = {
  teamMembers: 'learner.team.sections.teamMembers',
  phishingMetrics: 'learner.team.sections.phishingMetrics',
  phishingByMember: 'learner.team.sections.phishingByMember',
  trainingByMember: 'learner.team.sections.trainingByMember',
  announcementsByMember: 'learner.team.sections.announcementsByMember',
  phishingCampaigns: 'learner.team.sections.phishingCampaigns',
  trainingCourses: 'learner.team.sections.trainingCourses',
  announcementCampaigns: 'learner.team.sections.announcementCampaigns',
  managerOfManager: 'learner.team.sections.managerOfManager',
  pointsDistribution: 'learner.team.sections.pointsDistribution',
  badgeDistribution: 'learner.team.sections.badgeDistribution',
  recentAnnouncements: 'learner.team.sections.recentAnnouncements',
  activityHistory: 'learner.team.sections.activityHistory',
  weeklyRecap: 'learner.team.sections.weeklyRecap',
  levelLadder: 'learner.team.sections.levelLadder',
  memberActivity: 'learner.team.sections.memberActivity',
  // The Gamification switch itself (gamification-3 item 1): unreadable, the
  // points, badges and levels are hidden until it can be read.
  gamification: 'learner.team.sections.gamification',
  // The KPI cards' SQL totals (audit batch B, T20 remainder).
  phishingKpis: 'learner.team.sections.phishingKpis',
  trainingKpis: 'learner.team.sections.trainingKpis',
  announcementKpis: 'learner.team.sections.announcementKpis',
};

/**
 * One `badgeDistributionData` row exactly as the API sends it
 * (BadgeDistributionResponseDto in apps/api/src/modules/team-portal/dto/
 * team-dashboard-response.dto.ts). The view read `name` / `icon`, which the
 * API never sends, so every tile was a nameless broken image (audit T15); the
 * dashboard is read through `any`, so nothing said so. Pinned to the DTO file
 * by __tests__/team-dashboard-reads-the-api-shapes.spec.tsx.
 */
interface BadgeDistributionRow {
  badgeId: string;
  /** The badge's own name: tenant data, shown as written. */
  badgeName: string;
  badgeIcon: string;
  count: number;
}

// Extended TeamMember interface for My Team tab
interface MyTeamMember {
  id: string;
  name: string;
  email: string;
  avatar: string;
  department: string;
  designation: string;
  riskLevel: 'High' | 'Medium' | 'Low';
  riskScore: number;
  totalPoints: number;
  badgeCount: number;
  badges: string[];
  rank: number;
  rankInDepartment: number;
  rankInOrg: number;
  trend: number;
  lastActivity: string;
  isDirectReport: boolean;
  isIndirectReport: boolean;
  isManager: boolean;
  reportsTo?: string;
  managerName?: string;
  membersInTeam: number;
  timesCompromised: number;
  trainingCompletion: number;
  reportRate: number;
  phishing: {
    sent: number;
    opened: number;
    openedRate: number;
    clicked: number;
    clickedRate: number;
    compromised: number;
    compromisedRate: number;
    reported: number;
    reportedRate: number;
    trained: number;
    trainedRate: number;
    evaluated: number;
    evaluatedRate: number;
  };
  training: {
    assigned: number;
    completed: number;
    completedRate: number;
    inProgress: number;
    inProgressRate: number;
    trained: number;
    trainedRate: number;
    evaluated: number;
    evaluatedRate: number;
    expired: number;
    expiredRate: number;
  };
  announcement: {
    sent: number;
    opened: number;
    openedRate: number;
    acknowledged: number;
    acknowledgedRate: number;
    reported: number;
    reportedRate: number;
    trained: number;
    trainedRate: number;
    evaluated: number;
    evaluatedRate: number;
  };
}

const getRiskLevelColor = (level: string) => {
  switch (level) {
    case 'High':
      return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800';
    case 'Low':
      return 'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
    default:
      return 'bg-muted text-foreground border-border';
  }
};

// Helper function to compute risk factors for each employee
const getRiskFactors = (t: Translate, member: TeamMember): Array<{ label: string; color: string; colorClass: string }> => {
  const factors: Array<{ label: string; color: string; colorClass: string }> = [];
  
  // Training completion below 50%
  if ((member.trainingCompletion || 0) < 50) {
    factors.push({ label: t('learner.team.riskFactors.lowTraining'), color: 'red', colorClass: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800' });
  }

  // Overdue rate above 30%
  if ((member.overdueRate || 0) > 30) {
    factors.push({ label: t('learner.team.riskFactors.overdue'), color: 'orange', colorClass: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800' });
  }

  // Repeat compromises (2 or more)
  if ((member.timesCompromised || 0) >= 2) {
    factors.push({ label: t('learner.team.riskFactors.repeatCompromised'), color: 'red', colorClass: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800' });
  }

  // Low report rate (below 30%)
  if ((member.reportRate || 0) < 30) {
    factors.push({ label: t('learner.team.riskFactors.lowReporting'), color: 'yellow', colorClass: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-300 dark:border-yellow-800' });
  }

  // Negative trend (declining performance)
  if (member.trend < 0) {
    factors.push({ label: t('learner.team.riskFactors.declining'), color: 'orange', colorClass: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300 dark:border-orange-800' });
  }
  
  return factors;
};


// Re-export the shared monogram helper under the local alias used by this
// component (kept for the existing call sites — `getInitials(name)` is the
// long-standing API throughout this 3,987-line NC port).
const getInitials = (name: string | null | undefined): string =>
  getMonogram(name);

// Helper function to generate a consistent background color based on name
const getAvatarColor = (name: string): string => {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// Helper component to render employee avatar
const EmployeeAvatar = ({ name, avatar, className }: { name: string; avatar?: string; className?: string }) => {
  return (
    <Avatar className={className}>
      {avatar && <AvatarImage src={avatar} alt={name} />}
      <AvatarFallback className={`${getAvatarColor(name)} text-white font-semibold`}>
        {getInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
};

/**
 * A Badge Distribution picture, or its monogram when there is none to show. The
 * API sends '' for a badge with no icon (BadgeDistributionResponseDto maps a
 * null icon to ''), and an <img> with an empty src draws nothing (React warns
 * about it too); an icon URL that fails to load falls back the same way. The
 * stand-in keeps the picture's name (role="img" + aria-label), so a screen
 * reader hears the same thing either way. portal-preview.tsx guards its badges
 * alike (`icon && icon.trim()`). Levels and a member's own badges draw with the
 * shared <BadgeIcon>, which also resolves the global seed's Lucide icon names.
 */
const BadgePicture = ({
  src,
  name,
  className = '',
  monogramClassName = 'text-xs',
}: {
  src: string | null | undefined;
  name: string;
  className?: string;
  monogramClassName?: string;
}) => {
  const url = typeof src === 'string' ? src.trim() : '';
  const [failedUrl, setFailedUrl] = useState(null as string | null);
  if (url && failedUrl !== url) {
    return <img src={url} alt={name} className={className} onError={() => setFailedUrl(url)} />;
  }
  return (
    <span
      role="img"
      aria-label={name}
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground ${monogramClassName} ${className}`}
    >
      {getInitials(name)}
    </span>
  );
};

/**
 * The team endpoints' 403 `{ error: 'module_disabled' }` — the company does not
 * have the Team Portal module. Read off the ApiClientError that fetchApi
 * throws (`statusCode` + the response `body`).
 */
function isModuleDisabledError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { statusCode, body } = err as { statusCode?: unknown; body?: unknown };
  return (
    statusCode === 403 &&
    typeof body === 'object' &&
    body !== null &&
    (body as { error?: unknown }).error === 'module_disabled'
  );
}

interface ManagerDashboardContentProps {
  activeTab?: 'dashboard' | 'myteam';
  onTabChange?: (tab: 'dashboard' | 'myteam') => void;
  previewMode?: boolean;
  /**
   * If set, the component fetches dashboard data via the ADMIN endpoint
   * `/v1/team-portal/view-as/<managerId>` using the admin JWT (impersonation
   * preview at `/teams-portal/view-as/<id>`). When unset, the component
   * fetches via the PORTAL endpoint `/v1/portal/team/dashboard` using the
   * portal JWT (live employee `/portal` flow). The two endpoints return the
   * same shape; only the auth + scoping differ.
   */
  managerId?: string;
  // Dashboard section visibility
  showManagerProfile?: boolean;
  showDesignation?: boolean;
  showLeaderboardRank?: boolean;
  showTotalPoints?: boolean;
  showTeamStructure?: boolean;
  showDirectReports?: boolean;
  showIndirectReports?: boolean;
  showManagersUnder?: boolean;
  showHierarchyButton?: boolean;
  showPhishingPerformance?: boolean;
  showPhishingKPIs?: boolean;
  showPhishingFunnel?: boolean;
  showPhishingBreakdown?: boolean;
  showTrainingPerformance?: boolean;
  showTrainingKPIs?: boolean;
  showTopCompleters?: boolean;
  showNeedsImprovement?: boolean;
  showAnnouncementPerformance?: boolean;
  showAnnouncementKPIs?: boolean;
  showAnnouncementFunnel?: boolean;
  showGamification?: boolean;
  showLevels?: boolean;
  showBadgeDistribution?: boolean;
  showTeamLeaderboard?: boolean;
  // My Team sub-tab visibility
  showMyTeamLeaderboard?: boolean;
  showMyTeamRiskScore?: boolean;
  showMyTeamPhishing?: boolean;
  showMyTeamTraining?: boolean;
  showMyTeamAnnouncement?: boolean;
}

export function ManagerDashboardContent({
  activeTab, onTabChange, previewMode, managerId,
  showManagerProfile = true, showDesignation = true, showLeaderboardRank: showLeaderboardRankProp = true, showTotalPoints: showTotalPointsProp = true,
  showTeamStructure = true, showDirectReports = true, showIndirectReports = true, showManagersUnder = true, showHierarchyButton = true,
  showPhishingPerformance = true, showPhishingKPIs = true, showPhishingFunnel = true, showPhishingBreakdown = true,
  showTrainingPerformance = true, showTrainingKPIs = true, showTopCompleters = true, showNeedsImprovement = true,
  showAnnouncementPerformance = true, showAnnouncementKPIs = true, showAnnouncementFunnel = true,
  showGamification: showGamificationProp = true, showLevels = true, showBadgeDistribution = true, showTeamLeaderboard = true,
  showMyTeamLeaderboard: showMyTeamLeaderboardProp = true, showMyTeamRiskScore = true, showMyTeamPhishing = true, showMyTeamTraining = true, showMyTeamAnnouncement = true,
}: ManagerDashboardContentProps) {
  const router = useRouter();
  const { t, locale } = useTranslation();
  const isRtl = directionFor(locale) === 'rtl';

  // Dashboard data from API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  // The API refused with 403 module_disabled: the company does not have Team
  // Portal (audit 2026-09-25, T2). Shown as that, not as an empty team.
  const [moduleDisabled, setModuleDisabled] = useState(false);

  useEffect(() => {
    // Two contexts return the same 18-field TeamDashboardData shape:
    //   • Admin impersonation (`managerId` set): admin JWT -> /team-portal/view-as/<id>
    //   • Live employee (`managerId` undefined): portal JWT -> /portal/team/dashboard
    // Branching here keeps the rest of the component context-agnostic.
    const fetcher = managerId
      ? portalAdminFetch(`team-portal/view-as/${managerId}`)
      : fetchPortalApi('/portal/team/dashboard');
    fetcher
      .then((data) => {
        setDashboardData(data);
        setDashboardLoading(false);
      })
      .catch((err: unknown) => {
        setModuleDisabled(isModuleDisabledError(err));
        setDashboardLoading(false);
      });
  }, [managerId]);

  // Derive data from API response (wrapped in useMemo to avoid unstable references)
  const emptyManagerProfile = useMemo(() => ({ id: '', name: '', email: '', department: '', designation: '', rank: 0, totalPoints: 0, badges: [] as string[], avatar: '' }), []);
  const emptyPhishingMetrics = useMemo(() => ({ sent: 0, opened: 0, openedRate: 0, clicked: 0, clickedRate: 0, compromised: 0, compromisedRate: 0, reported: 0, reportedRate: 0, trained: 0, trainedRate: 0, evaluated: 0, evaluatedRate: 0 }), []);
  const emptyMyTeamManagerOfManager = useMemo(() => ({ id: '', name: '', email: '', designation: '' }), []);
  const emptyMyTeamManagerProfile = useMemo(() => ({ id: '', name: '', email: '', avatar: '', department: '', designation: '', reportsTo: '' }), []);
  const emptyGamificationHistory = useMemo(() => ({} as Record<string, GamificationHistoryEvent[]>), []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emptyArray = useMemo(() => [] as any[], []);

  // Permissive shape lets downstream callbacks ((c) => c.x, (acc, row) => …)
  // resolve their parameter types instead of silently inferring `any`. The
  // file historically lived under `// @ts-nocheck` because the chart layers
  // tack on extra fields (key/fill/fullName) the BE doesn't return — using a
  // permissive Record keeps both worlds typed without forcing every nested
  // shape to be enumerated. Tightening to concrete DTOs is a follow-up epic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type LooseRow = Record<string, any>;
  // Gamification switched off for the tenant (gamification-3 item 1): the API
  // sends no points, ranks, badges, levels or points history, and says so.
  // Hide those sections, as the employee portal and the admin previews do,
  // rather than draw them empty. A payload from before the flag reads as on.
  const gamificationOn = dashboardData?.gamificationEnabled !== false;
  const showLeaderboardRank = gamificationOn && showLeaderboardRankProp;
  const showTotalPoints = gamificationOn && showTotalPointsProp;
  const showGamification = gamificationOn && showGamificationProp;
  const showMyTeamLeaderboard = gamificationOn && showMyTeamLeaderboardProp;
  const managerProfile = dashboardData?.managerProfile ?? emptyManagerProfile;
  const allTeamMembers: TeamMember[] = dashboardData?.allTeamMembers ?? emptyArray;
  const phishingCampaigns: LooseRow[] =
    dashboardData?.phishingCampaigns ?? emptyArray;
  const trainingCourses: LooseRow[] =
    dashboardData?.trainingCourses ?? emptyArray;
  const announcementCampaigns: LooseRow[] =
    dashboardData?.announcementCampaigns ?? emptyArray;
  const phishingMetrics = dashboardData?.phishingMetrics ?? emptyPhishingMetrics;
  const myTeamManagerOfManager =
    dashboardData?.myTeamManagerOfManager ?? emptyMyTeamManagerOfManager;
  const myTeamManagerProfile =
    dashboardData?.myTeamManagerProfile ?? emptyMyTeamManagerProfile;
  // The BE returns members in `DashboardTeamMember` shape (flat fields only —
  // no nested phishing/training/announcement sub-objects). The FE's MyTeam
  // table reads `member.phishing.sent`, `member.training.assigned`, etc.
  // We backfill zero-defaults here so the table renders zeros instead of
  // crashing with "Cannot read properties of undefined (reading 'sent')".
  // Future epic: populate these aggregates per-member on the BE side.
  const emptyPhishingNested = useMemo(() => ({
    sent: 0, opened: 0, openedRate: 0,
    clicked: 0, clickedRate: 0,
    compromised: 0, compromisedRate: 0,
    reported: 0, reportedRate: 0,
    trained: 0, trainedRate: 0,
    evaluated: 0, evaluatedRate: 0,
  }), []);
  const emptyTrainingNested = useMemo(() => ({
    assigned: 0,
    completed: 0, completedRate: 0,
    inProgress: 0, inProgressRate: 0,
    notStarted: 0, notStartedRate: 0,
    trained: 0, trainedRate: 0,
    evaluated: 0, evaluatedRate: 0,
    expired: 0, expiredRate: 0,
    overdue: 0, overdueRate: 0,
  }), []);
  const emptyAnnouncementNested = useMemo(() => ({
    sent: 0, opened: 0, openedRate: 0,
    acknowledged: 0, acknowledgedRate: 0,
    reported: 0, reportedRate: 0,
    trained: 0, trainedRate: 0,
    evaluated: 0, evaluatedRate: 0,
  }), []);
  // The My Team tab's lists are the SAME lists the Dashboard tab reads: the
  // API sends each once (it used to send myTeam* copies of all four, doubling
  // the payload — audit 2026-09-25 batch B, T20 remainder).
  const myTeamAllMembers: MyTeamMember[] = useMemo(() => {
    const raw: LooseRow[] = dashboardData?.allTeamMembers ?? [];
    return raw.map((m) => ({
      ...m,
      phishing: { ...emptyPhishingNested, ...(m.phishing ?? {}) },
      training: { ...emptyTrainingNested, ...(m.training ?? {}) },
      announcement: { ...emptyAnnouncementNested, ...(m.announcement ?? {}) },
    })) as MyTeamMember[];
  }, [dashboardData?.allTeamMembers, emptyPhishingNested, emptyTrainingNested, emptyAnnouncementNested]);
  const myTeamPhishingCampaigns: LooseRow[] = phishingCampaigns;
  const myTeamTrainingCourses: LooseRow[] = trainingCourses;
  const myTeamAnnouncementCampaigns: LooseRow[] = announcementCampaigns;
  const pointsDistributionData: LooseRow[] =
    dashboardData?.pointsDistributionData ?? emptyArray;
  // The Campaign Funnel chart and its legend both draw `filteredFunnelData`
  // (the selected campaigns' stages, each with a key, a colour and a name in
  // the reader's language); the API's `funnelData` ({ stage, value }) is not
  // read here — the legend read key/fill/fullName off it, which it never
  // carries (audit T15).
  const badgeDistributionData: BadgeDistributionRow[] =
    dashboardData?.badgeDistributionData ?? emptyArray;
  // Sections the API could not read this time (audit T18) — shown empty, and
  // named in a notice so empty never reads as "no activity".
  const unavailableSections: string[] = Array.isArray(dashboardData?.unavailableSections)
    ? dashboardData.unavailableSections
    : emptyArray;
  // A member's OWN badges (their row's names, newest first — audit T9), each
  // with its icon where the team's Badge Distribution carries it.
  const badgeIconByName = new Map(
    badgeDistributionData.map((b) => [b.badgeName, b.badgeIcon] as const),
  );
  const ownBadges = (member: { badges?: string[] }) =>
    (member.badges ?? []).map((name) => ({ name, icon: badgeIconByName.get(name) ?? '' }));
  const recentAnnouncements: LooseRow[] =
    dashboardData?.recentAnnouncements ?? emptyArray;
  const employeeGamificationHistory =
    dashboardData?.employeeGamificationHistory ?? emptyGamificationHistory;
  // The workspace's level ladder (LevelsService.getLadder — the rungs every
  // member's own portal shows), lowest first. A member's level is the last rung
  // their points reach. This view used to carry its own Beginner 200 … Master
  // 1600 ladder, so a portal "Cadet" was a "Beginner" here (audit T13).
  const levelLadder = useMemo(
    () => sortLadder((dashboardData?.levelLadder ?? []) as Level[]),
    [dashboardData?.levelLadder],
  );
  const levelOf = (points: number): Level | null => earnedRung(levelLadder, points);
  // The weekly recap, exactly as the API sends it (WeeklyRecapResponseDto):
  // a flat summary of LAST week — the last completed week. This used to be
  // backfilled into nested gamification / campaign / training / announcement
  // objects the API never sent, and the zeros and blanks were shown as facts
  // (audit 2026-09-25 batch B). The modal reads only these fields now.
  const weeklyRecap: WeeklyRecap | null = dashboardData?.weeklyRecapData ?? null;
  const signedPoints = (n: number) => (n > 0 ? `+${n}` : String(n));

  // The week the recap covers, as the API counted it: [weekStart, weekEnd) is
  // Monday to Monday in the zone the API names (`timeZone`, the workspace's),
  // so the week's first and last CALENDAR days — the Monday and the Sunday
  // before the exclusive `weekEnd` — are printed in that zone ("Sep 14, 2026 –
  // Sep 20, 2026"), in the reader's language. The viewer's own zone would
  // shift them a day for anyone east or west of the workspace (T19). An API
  // that names no zone counted a UTC week. A missing or unreadable bound gives
  // '' — never "Invalid Date". The slides used to print a hard-coded
  // "November 2025" whatever the week.
  const recapPeriod = (() => {
    const zone = normalizeTimeZone(weeklyRecap?.timeZone) ?? 'UTC';
    const weekDay: Intl.DateTimeFormatOptions = { ...DATE, timeZone: zone };
    const from = formatLearnerDate(weeklyRecap?.weekStart, locale, weekDay);
    const endMs = weeklyRecap?.weekEnd ? Date.parse(weeklyRecap.weekEnd) : Number.NaN;
    const to = Number.isNaN(endMs) ? '' : formatLearnerDate(endMs - 1, locale, weekDay);
    return from && to ? t('learner.team.recap.period', { from, to }) : '';
  })();

  // Get unique departments from team members — strip W5 sentinel '' so Radix
  // <Select.Item> never receives an empty string value.
  const allDepartments = useMemo(() => [...new Set(allTeamMembers.map(m => m.department).filter(Boolean))] as string[], [allTeamMembers]);

  const [reporteeFilter, setReporteeFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [showSafest, setShowSafest] = useState(false);
  const [isHierarchyModalOpen, setIsHierarchyModalOpen] = useState(false);
  const [isEmployeeListModalOpen, setIsEmployeeListModalOpen] = useState(false);
  const [employeeListCategory, setEmployeeListCategory] = useState('all' as 'all' | 'direct' | 'indirect' | 'managers');
  const [currentPage, setCurrentPage] = useState(1);
  /** The id of the level whose members the Level Employees modal lists. */
  const [selectedLevel, setSelectedLevel] = useState(null as string | null);
  const [selectedBadge, setSelectedBadge] = useState(null as { name: string; icon: string; count: number } | null);
  const [selectedLeaderboardEmployee, setSelectedLeaderboardEmployee] = useState(null as TeamMember | null);
  const [activityHistoryTab, setActivityHistoryTab] = useState('badges' as 'badges' | 'levels' | 'points');
  const [activityDurationFilter, setActivityDurationFilter] = useState('all' as 'all' | '7days' | '30days' | '90days');
  // The weekly recap modal opens on its own at most once per recap week.
  // Why: in preview mode (admin builder) the modal must stay closed so
  // admins aren't blocked from seeing the dashboard underneath; live mode
  // gates the auto-open on a per-week localStorage flag so the modal can't
  // become an every-visit annoyance. The week is the one the API counted
  // (`weekStart`, the workspace's Monday), not the viewer's own ISO week,
  // which differs for a manager in another zone than the workspace.
  const [showWeeklyRecap, setShowWeeklyRecap] = useState(false);
  const [recapSlide, setRecapSlide] = useState(0);

  const hasWeeklyRecap = !!weeklyRecap;
  // Dismissal is for the week the recap describes (its `weekStart` instant),
  // not the browser's local week (audit T19; locale audit 2026-09-26).
  const recapWeekStart: string | undefined = weeklyRecap?.weekStart;
  useEffect(() => {
    if (previewMode || !hasWeeklyRecap || isWeeklyRecapDismissed(recapWeekStart)) return;
    setShowWeeklyRecap(true);
  }, [previewMode, hasWeeklyRecap, recapWeekStart]);

  const handleDismissWeeklyRecap = () => {
    setShowWeeklyRecap(false);
    persistWeeklyRecapDismissal(recapWeekStart);
  };
  const [visibleBars, setVisibleBars] = useState(['sent', 'opened', 'clicked', 'compromised', 'reported', 'trained', 'evaluated'] as string[]);
  const [visibleAnnouncementBars, setVisibleAnnouncementBars] = useState(['sent', 'opened', 'acknowledged', 'trained'] as string[]);

  // Campaign and Training filters
  const [selectedPhishingCampaigns, setSelectedPhishingCampaigns] = useState([] as string[]);
  const [selectedTrainingCourses, setSelectedTrainingCourses] = useState([] as string[]);

  // Announcement filter
  const [selectedAnnouncementCampaigns, setSelectedAnnouncementCampaigns] = useState([] as string[]);

  // Advanced filter drawer states
  const [phishingDrawerOpen, setPhishingDrawerOpen] = useState(false);
  const [trainingDrawerOpen, setTrainingDrawerOpen] = useState(false);
  const [announcementDrawerOpen, setAnnouncementDrawerOpen] = useState(false);

  // Risk table filters, sorting, and pagination
  const [riskTableSearch, setRiskTableSearch] = useState('');
  const [riskLevelFilter, setRiskLevelFilter] = useState('all' as 'all' | 'High' | 'Medium' | 'Low');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [riskTableSort, setRiskTableSort] = useState({ key: 'riskLevel', direction: 'desc' as 'asc' | 'desc' });
  const [riskTablePage, setRiskTablePage] = useState(0);
  const [riskTablePageSize, setRiskTablePageSize] = useState(10);

  const itemsPerPage = 5;
  // Gamification and phishing — the two things the weekly recap measures;
  // phishing alone while Gamification is off (gamification-3 item 1).
  const recapSlideKinds: ReadonlyArray<'gamification' | 'phishing'> = gamificationOn
    ? ['gamification', 'phishing']
    : ['phishing'];
  const totalRecapSlides = recapSlideKinds.length;
  const currentRecapSlide = recapSlideKinds[recapSlide % totalRecapSlides];

  // ========== MY TEAM TAB STATE ==========
  const [internalMainTab, setInternalMainTab] = useState('dashboard' as 'dashboard' | 'myteam');
  const mainTab = activeTab ?? internalMainTab;
  const setMainTab = (tab: 'dashboard' | 'myteam') => {
    setInternalMainTab(tab);
    onTabChange?.(tab);
  };
  const [myTeamSearchQuery, setMyTeamSearchQuery] = useState('');
  const [myTeamDepartmentFilter, setMyTeamDepartmentFilter] = useState('all');
  const [myTeamCurrentPage, setMyTeamCurrentPage] = useState(1);
  const [myTeamSelectedPhishingCampaigns, setMyTeamSelectedPhishingCampaigns] = useState([] as string[]);
  const [myTeamSelectedTrainingCourses, setMyTeamSelectedTrainingCourses] = useState([] as string[]);
  const [myTeamSelectedAnnouncementCampaigns, setMyTeamSelectedAnnouncementCampaigns] = useState([] as string[]);

  // My Team advanced filter drawer states
  const [myTeamPhishingDrawerOpen, setMyTeamPhishingDrawerOpen] = useState(false);
  const [myTeamTrainingDrawerOpen, setMyTeamTrainingDrawerOpen] = useState(false);
  const [myTeamAnnouncementDrawerOpen, setMyTeamAnnouncementDrawerOpen] = useState(false);

  const [myTeamActiveTab, setMyTeamActiveTab] = useState('leaderboard');
  // The Leaderboard sub-tab is the default; without it (Gamification off) the
  // table opens on Risk Score instead of on no tab at all.
  const myTeamTab =
    !showMyTeamLeaderboard && myTeamActiveTab === 'leaderboard' ? 'risk-score' : myTeamActiveTab;
  // Sort + rows-per-page for the My Team tab (tickets 852/853). Only fields
  // actually present on the team-member rows are offered — Name, Last Activity
  // and Points — so the dropdown never sorts on a fabricated column.
  const [myTeamSort, setMyTeamSort] = useState<'name' | 'lastActivity' | 'points'>('lastActivity');
  const [myTeamItemsPerPage, setMyTeamItemsPerPage] = useState(10);

  // Team filter state for My Team tab
  type MyTeamFilterType = 'all' | 'direct' | 'indirect' | 'myteam';
  const [myTeamFilter, setMyTeamFilter] = useState('all' as MyTeamFilterType);

  // View As state for My Team tab
  const [viewAsManager, setViewAsManager] = useState(null as MyTeamMember | null);
  const [viewAsHistory, setViewAsHistory] = useState([] as MyTeamMember[]);

  // Phishing/Announcement relative filters for My Team tab
  const [myTeamPhishingRelativeFilter, setMyTeamPhishingRelativeFilter] = useState('opened' as 'opened' | 'sent');
  const [myTeamAnnouncementRelativeFilter, setMyTeamAnnouncementRelativeFilter] = useState('opened' as 'opened' | 'sent');
  // ========== END MY TEAM TAB STATE ==========

  // Sync filter selections when dashboard data loads
  useEffect(() => {
    if (dashboardData) {
      if (phishingCampaigns.length > 0 && selectedPhishingCampaigns.length === 0) {
        setSelectedPhishingCampaigns(phishingCampaigns.map((c) => String(c.id)));
      }
      if (trainingCourses.length > 0 && selectedTrainingCourses.length === 0) {
        setSelectedTrainingCourses(trainingCourses.map((t) => String(t.id)));
      }
      if (announcementCampaigns.length > 0 && selectedAnnouncementCampaigns.length === 0) {
        setSelectedAnnouncementCampaigns(announcementCampaigns.map((a) => String(a.id)));
      }
      if (myTeamPhishingCampaigns.length > 0 && myTeamSelectedPhishingCampaigns.length === 0) {
        setMyTeamSelectedPhishingCampaigns(myTeamPhishingCampaigns.map((c) => String(c.id)));
      }
      if (myTeamTrainingCourses.length > 0 && myTeamSelectedTrainingCourses.length === 0) {
        setMyTeamSelectedTrainingCourses(myTeamTrainingCourses.map((t) => String(t.id)));
      }
      if (myTeamAnnouncementCampaigns.length > 0 && myTeamSelectedAnnouncementCampaigns.length === 0) {
        setMyTeamSelectedAnnouncementCampaigns(myTeamAnnouncementCampaigns.map((a) => String(a.id)));
      }
    }
  }, [dashboardData]);

  // ========== MY TEAM TAB COMPUTED VALUES & HELPERS ==========
  
  // Current manager being viewed (either the logged-in manager or a viewAs target)
  const myTeamCurrentManager = viewAsManager ? {
    id: viewAsManager.id,
    name: viewAsManager.name,
    email: viewAsManager.email,
    avatar: viewAsManager.avatar,
    department: viewAsManager.department,
    designation: viewAsManager.designation,
    reportsTo: viewAsManager.reportsTo,
  } : myTeamManagerProfile;

  // Get the manager that the current manager reports to.
  // `myTeamManagerProfile.id` is the top-of-tree manager (the recipient who
  // owns this dashboard). When viewAsManager.reportsTo points to that id,
  // we return their profile directly; otherwise we look up the named
  // manager in the local list.
  const getMyTeamCurrentManagersManager = () => {
    if (viewAsManager) {
      if (viewAsManager.reportsTo === myTeamManagerProfile.id) {
        return myTeamManagerProfile;
      }
      const manager = myTeamAllMembers.find(m => m.id === viewAsManager.reportsTo);
      if (manager) {
        return { name: manager.name, designation: manager.designation };
      }
      return myTeamManagerOfManager;
    }
    return myTeamManagerOfManager;
  };

  // The My Team drawers narrow each member's NUMBERS to the ticked campaigns,
  // courses and announcements; the rows stay the team. The ticks used to be
  // read by nothing but the drawers' own badges (audit 2026-09-25, wave 3
  // portal-team-3 item 8). The dashboard route totals each member's numbers in
  // SQL for the ids it is given, so a narrowed selection asks it again with
  // them — the rule the KPI cards follow (lib/teams-portal/team-kpis.ts):
  // every row ticked → the numbers already loaded; some → the API's numbers
  // for those ids; none → nothing to count.
  const myTeamStatsQuery = useMemo(
    () =>
      teamKpiQuery({
        campaigns: { selectedIds: myTeamSelectedPhishingCampaigns, listed: myTeamPhishingCampaigns.length },
        courses: { selectedIds: myTeamSelectedTrainingCourses, listed: myTeamTrainingCourses.length },
        announcements: { selectedIds: myTeamSelectedAnnouncementCampaigns, listed: myTeamAnnouncementCampaigns.length },
      }),
    [
      myTeamSelectedPhishingCampaigns, myTeamPhishingCampaigns.length,
      myTeamSelectedTrainingCourses, myTeamTrainingCourses.length,
      myTeamSelectedAnnouncementCampaigns, myTeamAnnouncementCampaigns.length,
    ],
  );
  // Kept WITH the query it answers, so one selection's numbers never show
  // under another while the next answer loads.
  const [myTeamNarrowed, setMyTeamNarrowed] = useState<{ query: string; members: LooseRow[] } | null>(null);
  useEffect(() => {
    if (myTeamStatsQuery === null) return;
    let cancelled = false;
    const fetcher = managerId
      ? portalAdminFetch(`team-portal/view-as/${managerId}?${myTeamStatsQuery}`)
      : fetchPortalApi(`/portal/team/dashboard?${myTeamStatsQuery}`);
    fetcher
      .then((data: unknown) => {
        const members = (data as { allTeamMembers?: unknown } | null)?.allTeamMembers;
        if (!cancelled) {
          setMyTeamNarrowed({ query: myTeamStatsQuery, members: Array.isArray(members) ? (members as LooseRow[]) : [] });
        }
      })
      .catch(() => {
        if (!cancelled) setMyTeamNarrowed({ query: myTeamStatsQuery, members: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [myTeamStatsQuery, managerId]);
  const withMyTeamSelection = useMemo(() => {
    const narrowedById = new Map(
      (myTeamNarrowed !== null && myTeamNarrowed.query === myTeamStatsQuery ? myTeamNarrowed.members : []).map(
        (row) => [String(row.id), row] as const,
      ),
    );
    // One list's ticks, for one member's numbers.
    const pick = <T extends object>(selectedIds: string[], listed: number, loaded: T, empty: T, narrowed: unknown): T => {
      if (selectedIds.length >= listed) return loaded;
      if (selectedIds.length === 0) return empty;
      return { ...empty, ...((narrowed as Partial<T> | undefined) ?? {}) };
    };
    return (member: MyTeamMember): MyTeamMember => {
      const narrowed = narrowedById.get(member.id);
      return {
        ...member,
        phishing: pick(myTeamSelectedPhishingCampaigns, myTeamPhishingCampaigns.length, member.phishing, emptyPhishingNested, narrowed?.phishing),
        training: pick(myTeamSelectedTrainingCourses, myTeamTrainingCourses.length, member.training, emptyTrainingNested, narrowed?.training),
        announcement: pick(myTeamSelectedAnnouncementCampaigns, myTeamAnnouncementCampaigns.length, member.announcement, emptyAnnouncementNested, narrowed?.announcement),
      };
    };
  }, [
    myTeamNarrowed, myTeamStatsQuery,
    myTeamSelectedPhishingCampaigns, myTeamPhishingCampaigns.length,
    myTeamSelectedTrainingCourses, myTeamTrainingCourses.length,
    myTeamSelectedAnnouncementCampaigns, myTeamAnnouncementCampaigns.length,
    emptyPhishingNested, emptyTrainingNested, emptyAnnouncementNested,
  ]);

  // Get team members for the current view, each with its numbers for what
  // the My Team drawers have ticked.
  const myTeamCurrentTeamMembers = useMemo(() => {
    const inView = viewAsManager
      ? myTeamAllMembers.filter(m => m.reportsTo === viewAsManager.id)
      : myTeamAllMembers;
    return inView.map(withMyTeamSelection);
  }, [viewAsManager, myTeamAllMembers, withMyTeamSelection]);

  // No-op until the portal-side "view-as" lands. The target route
  // (/teams-portal/view-as/:id) was a dead link; admin-side view-as
  // lives under /settings/team-portal/*.
  const handleViewAs = (_member: MyTeamMember) => {
    // intentionally empty
  };

  // Handle going back to previous view
  const handleBackToMyTeam = () => {
    setViewAsManager(null);
    setViewAsHistory([]);
    setMyTeamCurrentPage(1);
    setMyTeamFilter('all');
  };

  // Get unique departments for My Team tab.
  // Filter out empty / null / undefined values: Radix <Select.Item> THROWS
  // when value="" (it uses the empty string as a special "clear selection"
  // signal). When a reportee has no department in the DB, including them
  // would crash the entire My Team tab with "A <Select.Item /> must have a
  // value prop that is not an empty string."
  const myTeamDepartments = useMemo(() => {
    const depts = new Set(
      myTeamAllMembers
        .map((m) => m.department)
        .filter((d): d is string => !!d && d.length > 0),
    );
    return Array.from(depts);
  }, [myTeamAllMembers]);

  // Calculate team filter counts based on current view.
  // Use the BE-computed `isDirectReport`/`isManager` flags (the same source the
  // Dashboard tab's Team Structure card uses) so both tabs reconcile. The old
  // logic compared `member.reportsTo` (an EMAIL — the DTO sets reportsTo =
  // managerEmail) against `myTeamCurrentManager.id` (a UUID); they never
  // matched, so `direct` was always 0 and everyone counted as indirect.
  // "Indirect" mirrors the Dashboard definition: any reportee that is not a
  // direct report (i.e. `!isDirectReport`).
  const myTeamFilterCounts = useMemo(() => ({
    all: myTeamCurrentTeamMembers.length,
    direct: myTeamCurrentTeamMembers.filter(m => m.isDirectReport).length,
    indirect: myTeamCurrentTeamMembers.filter(m => !m.isDirectReport).length,
    myteam: myTeamCurrentTeamMembers.filter(m => m.isManager).length,
  }), [myTeamCurrentTeamMembers]);

  // Filter members based on team type, search, and department
  const myTeamFilteredMembers = useMemo(() => {
    const filtered = myTeamCurrentTeamMembers.filter((member) => {
      if (myTeamFilter === 'direct' && !member.isDirectReport) return false;
      if (myTeamFilter === 'indirect' && member.isDirectReport) return false;
      if (myTeamFilter === 'myteam' && !member.isManager) return false;

      const q = myTeamSearchQuery.toLowerCase();
      const matchesSearch =
        myTeamSearchQuery === '' ||
        (member.name ?? '').toLowerCase().includes(q) ||
        (member.email ?? '').toLowerCase().includes(q) ||
        (member.department ?? '').toLowerCase().includes(q) ||
        (member.designation ?? '').toLowerCase().includes(q);

      const matchesDepartment =
        myTeamDepartmentFilter === 'all' || member.department === myTeamDepartmentFilter;

      return matchesSearch && matchesDepartment;
    });

    const sorted = [...filtered];
    if (myTeamSort === 'name') {
      sorted.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    } else if (myTeamSort === 'points') {
      sorted.sort((a, b) => b.totalPoints - a.totalPoints);
    } else {
      // lastActivity — most recent first; unparseable dates sink to the bottom.
      sorted.sort((a, b) => {
        const ta = Date.parse(a.lastActivity ?? '');
        const tb = Date.parse(b.lastActivity ?? '');
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
    }
    return sorted;
  }, [myTeamCurrentTeamMembers, myTeamFilter, myTeamSearchQuery, myTeamDepartmentFilter, myTeamSort]);

  // Pagination for My Team tab
  const myTeamActiveTabMembers = myTeamFilteredMembers;
  const myTeamTotalPages = Math.ceil(myTeamActiveTabMembers.length / myTeamItemsPerPage);
  const myTeamPaginatedMembers = myTeamActiveTabMembers.slice(
    (myTeamCurrentPage - 1) * myTeamItemsPerPage,
    myTeamCurrentPage * myTeamItemsPerPage
  );

  // Get hierarchy chain for a member
  const getHierarchyChain = (member: MyTeamMember): string[] => {
    const chain: string[] = [];
    if (member.reportsTo) {
      const manager = myTeamAllMembers.find(m => m.id === member.reportsTo);
      if (manager) {
        chain.push(myTeamManagerProfile.name);
        if (manager.reportsTo && manager.reportsTo !== myTeamManagerProfile.id) {
          const grandManager = myTeamAllMembers.find(m => m.id === manager.reportsTo);
          if (grandManager) {
            chain.push(grandManager.name);
          }
        }
        chain.push(manager.name);
      } else if (member.reportsTo === myTeamManagerProfile.id) {
        chain.push(myTeamManagerProfile.name);
      }
    }
    chain.push(member.name);
    return chain;
  };

  // Get truncated hierarchy display. Each name is isolated in right-to-left, so
  // the separator takes the page's direction and its arrow (← in Arabic)
  // points from each manager to the next level down.
  const joinChain = (names: string[]) =>
    names.map((n) => (isRtl ? `\u2068${n}\u2069` : n)).join(t('learner.team.myTeam.hierarchySeparator'));
  const getHierarchyDisplay = (member: MyTeamMember) => {
    const chain = getHierarchyChain(member);
    const fullHierarchy = joinChain(chain);
    if (chain.length <= 2) {
      return { display: fullHierarchy, extra: 0, fullHierarchy };
    }
    const displayPart = joinChain(chain.slice(0, 2));
    const extraCount = chain.length - 2;
    return { display: displayPart, extra: extraCount, fullHierarchy };
  };

  // Get risk score badge
  const getRiskScoreBadge = (riskLevel: string, riskScore: number) => {
    let bgColor = '';
    let textColor = '';
    if (riskLevel === 'Low') {
      bgColor = 'bg-green-100 dark:bg-green-950';
      textColor = 'text-green-700 dark:text-green-300';
    } else if (riskLevel === 'Medium') {
      bgColor = 'bg-yellow-100 dark:bg-yellow-950';
      textColor = 'text-yellow-700 dark:text-yellow-300';
    } else {
      bgColor = 'bg-red-100 dark:bg-red-950';
      textColor = 'text-red-700 dark:text-red-300';
    }
    return (
      <Badge variant="outline" className={`${bgColor} ${textColor} border-0 font-medium px-3 py-1`}>
        {t('learner.team.myTeam.riskScoreBadge', { score: riskScore, level: riskLevelText(t, riskLevel) })}
      </Badge>
    );
  };

  // Hierarchy cell component with tooltip
  const HierarchyCell = ({ member }: { member: MyTeamMember }) => {
    const { display, extra, fullHierarchy } = getHierarchyDisplay(member);
    if (extra === 0) {
      return <span className="text-sm text-muted-foreground whitespace-nowrap">{display}</span>;
    }
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
              <span className="text-sm text-muted-foreground">{display}</span>
              <Badge variant="secondary" className="text-xs px-1.5 py-0 h-5 font-medium shrink-0">
                +{extra}
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="text-sm">{fullHierarchy}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Open a team member. In the admin "View as Manager" preview (`managerId`
  // set, admin JWT) that is the admin's preview of the employee. In the LIVE
  // portal the manager is an employee, not an admin — /user-portal/<id> is an
  // admin page and proxy.ts sent them to /login — so the portal's own member
  // view opens instead: the member dialog the Team Leaderboard uses (audit T14).
  const openTeamMember = (member: TeamMember) => {
    if (managerId) {
      router.push(`/user-portal/${member.id}`);
      return;
    }
    // The member dialog is their level, points, badges and points history —
    // with Gamification off there is nothing in it to show (gamification-3
    // item 1).
    if (!gamificationOn) return;
    setSelectedLeaderboardEmployee(member);
  };
  // ========== END MY TEAM TAB COMPUTED VALUES & HELPERS ==========

  // Auto-advance carousel
  useEffect(() => {
    if (!showWeeklyRecap) return;
    const timer = setInterval(() => {
      setRecapSlide((prev) => (prev + 1) % totalRecapSlides);
    }, 8000);
    return () => clearInterval(timer);
  }, [showWeeklyRecap]);

  // Filter team members based on the scope dropdown (reporteeFilter) AND the
  // department selector. Threading `departmentFilter` here makes every panel
  // derived from `filteredMembers` — the KPI cards, phishing/training/
  // announcement breakdown lists, Top/Vulnerable/Laggard buckets, leaderboard
  // and levels — respond to the department selector, so the Dashboard tab
  // filters consistently with the My Team tab (tickets 867, 1111).
  const filteredMembers = useMemo(() => {
    let scoped: TeamMember[];
    switch (reporteeFilter) {
      case 'direct':
        scoped = allTeamMembers.filter((m) => m.isDirectReport);
        break;
      case 'indirect':
        scoped = allTeamMembers.filter((m) => !m.isDirectReport);
        break;
      case 'managers':
        scoped = allTeamMembers.filter((m) => m.isManager);
        break;
      default:
        scoped = allTeamMembers;
    }
    if (departmentFilter !== 'all') {
      scoped = scoped.filter((m) => m.department === departmentFilter);
    }
    return scoped;
  }, [reporteeFilter, allTeamMembers, departmentFilter]);

  // Calculate stats. The Team Structure KPI cards (Total Reportees / Direct /
  // Indirect / Managers) are derived from `filteredMembers` so the scope
  // dropdown and department selector actually re-scope the top KPIs (tickets
  // 867, 1111) instead of always reporting the full `allTeamMembers` set.
  const stats = useMemo(() => {
    const direct = filteredMembers.filter((m) => m.isDirectReport).length;
    const managers = filteredMembers.filter((m) => m.isManager).length;
    const indirect = filteredMembers.filter((m) => !m.isDirectReport).length;
    const highRisk = filteredMembers.filter((m) => m.riskLevel === 'High').length;
    const mediumRisk = filteredMembers.filter((m) => m.riskLevel === 'Medium').length;
    const lowRisk = filteredMembers.filter((m) => m.riskLevel === 'Low').length;
    const totalPoints = filteredMembers.reduce((sum, m) => sum + m.totalPoints, 0);
    const avgPoints = filteredMembers.length > 0 ? Math.round(totalPoints / filteredMembers.length) : 0;
    const totalBadges = filteredMembers.reduce((sum, m) => sum + m.badgeCount, 0);
    const avgRank = filteredMembers.length > 0 ? Math.round(
      filteredMembers.reduce((sum, m) => sum + m.rank, 0) / filteredMembers.length
    ) : 0;

    return {
      total: filteredMembers.length,
      direct,
      indirect,
      managers,
      highRisk,
      mediumRisk,
      lowRisk,
      totalPoints,
      avgPoints,
      totalBadges,
      avgRank,
    };
  }, [filteredMembers]);

  // The KPI cards read SQL totals the API sends — never a sum of list rows
  // (audit 2026-09-25 batch B, T20 remainder). The campaign, course and
  // announcement lists stop at 500 rows, and adding up the ticked rows here
  // gave a team past 500 the newest 500's numbers. Every row ticked → the
  // dashboard's own kpiTotals (all rows); some ticked → GET …/kpis for those
  // ids; none ticked → nothing to count. See lib/teams-portal/team-kpis.ts.
  const kpiQuery = useMemo(
    () =>
      teamKpiQuery({
        campaigns: { selectedIds: selectedPhishingCampaigns, listed: phishingCampaigns.length },
        courses: { selectedIds: selectedTrainingCourses, listed: trainingCourses.length },
        announcements: { selectedIds: selectedAnnouncementCampaigns, listed: announcementCampaigns.length },
      }),
    [
      selectedPhishingCampaigns, phishingCampaigns.length,
      selectedTrainingCourses, trainingCourses.length,
      selectedAnnouncementCampaigns, announcementCampaigns.length,
    ],
  );
  const [narrowedKpis, setNarrowedKpis] = useState<TeamKpiTotals | null>(null);
  useEffect(() => {
    if (kpiQuery === null) {
      setNarrowedKpis(null);
      return;
    }
    let cancelled = false;
    const fetcher = managerId
      ? portalAdminFetch(`team-portal/view-as/${managerId}/kpis?${kpiQuery}`)
      : fetchPortalApi(`/portal/team/kpis?${kpiQuery}`);
    fetcher
      .then((data: unknown) => {
        if (!cancelled) setNarrowedKpis(data as TeamKpiTotals);
      })
      .catch(() => {
        if (!cancelled) setNarrowedKpis(null);
      });
    return () => {
      cancelled = true;
    };
  }, [kpiQuery, managerId]);
  const kpiTotals: TeamKpiTotals | undefined =
    kpiQuery === null ? (dashboardData?.kpiTotals as TeamKpiTotals | undefined) : (narrowedKpis ?? undefined);

  const filteredPhishingMetrics = useMemo(
    () =>
      selectedPhishingCampaigns.length === 0
        ? EMPTY_PHISHING_KPIS
        : (kpiTotals?.phishing ?? EMPTY_PHISHING_KPIS),
    [selectedPhishingCampaigns.length, kpiTotals],
  );

  const filteredTrainingMetrics = useMemo(
    () =>
      selectedTrainingCourses.length === 0
        ? EMPTY_TRAINING_KPIS
        : (kpiTotals?.training ?? EMPTY_TRAINING_KPIS),
    [selectedTrainingCourses.length, kpiTotals],
  );

  // Create funnel data from filtered metrics
  const filteredFunnelData = useMemo(() => [
    { name: t('learner.team.metrics.sent'), fullName: t('learner.team.metrics.sent'), value: filteredPhishingMetrics.sent, fill: 'var(--muted-foreground)', key: 'sent' },
    { name: t('learner.team.metrics.opened'), fullName: t('learner.team.metrics.opened'), value: filteredPhishingMetrics.opened, fill: '#fbbf24', key: 'opened' },
    { name: t('learner.team.metrics.clicked'), fullName: t('learner.team.metrics.clicked'), value: filteredPhishingMetrics.clicked, fill: '#ef4444', key: 'clicked' },
    { name: t('learner.team.metrics.compromised'), fullName: t('learner.team.metrics.compromised'), value: filteredPhishingMetrics.compromised, fill: '#ef4444', key: 'compromised' },
    { name: t('learner.team.metrics.reported'), fullName: t('learner.team.metrics.reported'), value: filteredPhishingMetrics.reported, fill: '#22c55e', key: 'reported' },
    { name: t('learner.team.metrics.trained'), fullName: t('learner.team.metrics.trained'), value: filteredPhishingMetrics.trained, fill: '#3b82f6', key: 'trained' },
    { name: t('learner.team.metrics.evaluated'), fullName: t('learner.team.metrics.evaluated'), value: filteredPhishingMetrics.evaluated, fill: '#8b5cf6', key: 'evaluated' },
  ], [filteredPhishingMetrics, t]);

  // Announcement KPI cards — the API's totals too (see kpiQuery above).
  const filteredAnnouncementMetrics = useMemo(
    () =>
      selectedAnnouncementCampaigns.length === 0
        ? EMPTY_ANNOUNCEMENT_KPIS
        : (kpiTotals?.announcements ?? EMPTY_ANNOUNCEMENT_KPIS),
    [selectedAnnouncementCampaigns.length, kpiTotals],
  );

  // Create funnel data for announcement metrics
  const announcementFunnelData = useMemo(() => [
    { name: t('learner.team.metrics.sent'), fullName: t('learner.team.metrics.sent'), value: filteredAnnouncementMetrics.sent, fill: 'var(--foreground)', key: 'sent' },
    { name: t('learner.team.metrics.opened'), fullName: t('learner.team.metrics.opened'), value: filteredAnnouncementMetrics.opened, fill: '#fbbf24', key: 'opened' },
    { name: t('learner.team.metrics.acknowledged'), fullName: t('learner.team.metrics.acknowledged'), value: filteredAnnouncementMetrics.acknowledged, fill: '#4ade80', key: 'acknowledged' },
    { name: t('learner.team.metrics.trained'), fullName: t('learner.team.metrics.trained'), value: filteredAnnouncementMetrics.trained, fill: '#3b82f6', key: 'trained' },
  ], [filteredAnnouncementMetrics, t]);

  // Risk distribution for pie chart
  const riskDistributionData = [
    { name: t('learner.team.risk.highRisk'), value: stats.highRisk, color: '#ef4444' },
    { name: t('learner.team.risk.mediumRisk'), value: stats.mediumRisk, color: '#f59e0b' },
    { name: t('learner.team.risk.lowRisk'), value: stats.lowRisk, color: '#22c55e' },
  ];

  // High risk members
  const highRiskMembers = filteredMembers.filter((m) => m.riskLevel === 'High');

  // Safest members (Low risk, sorted by points desc)
  const safestMembers = filteredMembers
    .filter((m) => m.riskLevel === 'Low')
    .sort((a, b) => b.totalPoints - a.totalPoints)
    .slice(0, 10);

  // Mutually-exclusive risk classification (ticket 995): assign each member to
  // exactly one bucket in descending severity so the same person can't appear
  // in Repeat Offenders AND Vulnerable AND Laggards at once. A member already
  // claimed by a higher-severity bucket is removed before the next filter.
  const classifiedIds = new Set<string>();

  // Repeat offenders (compromised 2+ times) — highest severity.
  const repeatOffenders = filteredMembers
    .filter((m) => (m.timesCompromised || 0) >= 2)
    .sort((a, b) => (b.timesCompromised || 0) - (a.timesCompromised || 0));
  repeatOffenders.forEach((m) => classifiedIds.add(m.id));

  // Vulnerable members (report rate below 30%), excluding repeat offenders.
  const vulnerableMembers = filteredMembers
    .filter((m) => !classifiedIds.has(m.id) && (m.reportRate || 0) < 30)
    .sort((a, b) => (a.reportRate || 0) - (b.reportRate || 0));
  vulnerableMembers.forEach((m) => classifiedIds.add(m.id));

  // Laggards (training completion below 50%), excluding the above buckets.
  const laggardMembers = filteredMembers
    .filter((m) => !classifiedIds.has(m.id) && (m.trainingCompletion || 0) < 50)
    .sort((a, b) => (a.trainingCompletion || 0) - (b.trainingCompletion || 0));
  laggardMembers.forEach((m) => classifiedIds.add(m.id));

  // Top performers = Low-risk members not claimed by any risk bucket above,
  // ranked by report rate desc. Computed here (not inline) so it excludes
  // members already surfaced as at-risk. We also require a real report-rate
  // threshold (>= 30%) rather than relying only on `riskLevel === 'Low'`, so a
  // Low-risk employee with a 0% report rate is never surfaced as a top
  // performer (ticket 870).
  const TOP_PERFORMER_MIN_REPORT_RATE = 30;
  const topPerformers = filteredMembers
    .filter(
      (m) =>
        !classifiedIds.has(m.id) &&
        m.riskLevel === 'Low' &&
        (m.reportRate || 0) >= TOP_PERFORMER_MIN_REPORT_RATE,
    )
    .sort((a, b) => (b.reportRate || 0) - (a.reportRate || 0))
    .slice(0, 5);

  // Members to display in table based on toggle
  const displayedMembers = showSafest ? safestMembers : highRiskMembers;

  // Filtered, sorted, and paginated risk members
  const filteredRiskMembers = useMemo(() => {
    // Start with either high risk or low risk based on toggle
    let data = showSafest 
      ? filteredMembers.filter(m => m.riskLevel === 'Low')
      : filteredMembers.filter(m => m.riskLevel === 'High' || m.riskLevel === 'Medium');
    
    // Apply search filter
    if (riskTableSearch) {
      const query = riskTableSearch.toLowerCase();
      data = data.filter(m => 
        m.name.toLowerCase().includes(query) || 
        m.email.toLowerCase().includes(query)
      );
    }
    
    // Apply risk level filter (within the already filtered set)
    if (riskLevelFilter !== 'all') {
      data = data.filter(m => m.riskLevel === riskLevelFilter);
    }
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      data = data.filter(m => m.department === departmentFilter);
    }
    
    // Apply sorting
    data.sort((a, b) => {
      const { key, direction } = riskTableSort;
      let aVal: any, bVal: any;
      
      switch (key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'riskLevel':
          const riskOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          aVal = riskOrder[a.riskLevel];
          bVal = riskOrder[b.riskLevel];
          break;
        case 'trainingCompletion':
          aVal = a.trainingCompletion || 0;
          bVal = b.trainingCompletion || 0;
          break;
        case 'reportRate':
          aVal = a.reportRate || 0;
          bVal = b.reportRate || 0;
          break;
        case 'timesCompromised':
          aVal = a.timesCompromised || 0;
          bVal = b.timesCompromised || 0;
          break;
        case 'badgeCount':
          aVal = a.badgeCount;
          bVal = b.badgeCount;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return data;
  }, [filteredMembers, showSafest, riskTableSearch, riskLevelFilter, departmentFilter, riskTableSort]);

  const riskTablePageCount = Math.ceil(filteredRiskMembers.length / riskTablePageSize);
  
  const paginatedRiskMembers = useMemo(() => {
    return filteredRiskMembers.slice(
      riskTablePage * riskTablePageSize,
      (riskTablePage + 1) * riskTablePageSize
    );
  }, [filteredRiskMembers, riskTablePage, riskTablePageSize]);

  // Handle sort click
  const handleRiskTableSort = (key: string) => {
    setRiskTableSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // The points band a rung of the workspace's ladder covers, in the reader's
  // language — lib/gamification/level-ladder.ts rungBand's rule: from its
  // threshold up to one below the next higher rung ("100–499 pts"), open-ended
  // for the top rung ("500+ pts"), and just the threshold for a rung the next
  // one shares (never held: the balance holds the later rung).
  const rungBandText = (index: number): string => {
    const lower = levelLadder[index].pointsRequired;
    const next = levelLadder[index + 1];
    if (!next) return t('learner.team.levels.bandTop', { lower });
    if (next.pointsRequired <= lower) return t('learner.team.levels.bandExact', { lower });
    return t('learner.team.levels.band', { lower, upper: next.pointsRequired - 1 });
  };

  // Handle legend click to toggle bar visibility
  const handleLegendClick = (key: string) => {
    setVisibleBars((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Handle announcement legend click to toggle bar visibility
  const handleAnnouncementLegendClick = (key: string) => {
    setVisibleAnnouncementBars((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Badge images for display. Points at the generated medallion set — the flat
  // /badges/*.png this used before were a different art style, and
  // completed-training.png does not exist (the file is .jpeg), so it rendered a
  // broken image here too.
  const badgeImages = [
    '/badges/medallions/first-steps.png',
    '/badges/medallions/quick-reporter.png',
    '/badges/medallions/phishing-spotter.png',
    '/badges/medallions/guardian.png',
    '/badges/medallions/scholar.png',
  ];

  // Get employees for modal based on category
  const getEmployeesForCategory = () => {
    switch (employeeListCategory) {
      case 'all':
        return allTeamMembers;
      case 'direct':
        return allTeamMembers.filter((m) => m.isDirectReport);
      case 'indirect':
        return allTeamMembers.filter((m) => !m.isDirectReport);
      case 'managers':
        return allTeamMembers.filter((m) => m.isManager);
      default:
        return allTeamMembers;
    }
  };

  // Handler to open employee list modal
  const handleOpenEmployeeList = (category: 'all' | 'direct' | 'indirect' | 'managers') => {
    setEmployeeListCategory(category);
    setIsEmployeeListModalOpen(true);
  };

  if (dashboardLoading) {
    return (
      <div className="flex-1 space-y-6 p-8 pt-6 bg-background">
        {/* Tabs + filter row */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28 rounded-lg" />
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-32 rounded-lg" />
            <Skeleton className="h-9 w-32 rounded-lg" />
          </div>
        </div>
        {/* Manager profile card */}
        <div className="rounded-xl border bg-card p-6 flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        {/* KPI row — 4 cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border bg-card p-4 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-24" />
            </div>
          ))}
        </div>
        {/* Two chart panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-xl border bg-card p-6 space-y-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-48 w-full" />
              <div className="flex gap-2 flex-wrap">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
        {/* Team leaderboard rows */}
        <div className="rounded-xl border bg-card p-6 space-y-3">
          <Skeleton className="h-5 w-36" />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (moduleDisabled) {
    // The product's "module not switched on" page. No way out is offered: a
    // manager here is on the portal, where the admin dashboard is not theirs.
    return (
      <div className="flex flex-1 flex-col bg-background">
        <StatusPanel
          kind="module-off"
          headingLevel={2}
          title={t('learner.team.moduleDisabled.title')}
          body={t('learner.team.moduleDisabled.body')}
          primary={null}
          secondary={null}
        />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex-1 space-y-6 p-8 pt-6 bg-background">
            {unavailableSections.length > 0 && (
              <div
                role="status"
                className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>
                  {t('learner.team.sections.notice', {
                    sections: unavailableSections
                      .map((name) => (SECTION_LABELS[name] ? t(SECTION_LABELS[name]) : name))
                      .join(t('learner.team.sections.separator')),
                  })}
                </p>
              </div>
            )}
            {/* Main Dashboard/My Team Tabs */}
            <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'dashboard' | 'myteam')} className="space-y-6">
              {/* Page Header with Tabs and Filters */}
              <div className="flex items-center justify-between">
                <TabsList aria-label={t('learner.team.tabs.label')} className="h-auto p-0 bg-transparent gap-1">
                  <TabsTrigger value="dashboard" className="px-4 py-2 text-sm font-medium rounded-lg data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50">
                    {t('learner.team.tabs.dashboard')}
                  </TabsTrigger>
                  <TabsTrigger value="myteam" className="px-4 py-2 text-sm font-medium rounded-lg data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/50">
                    {t('learner.team.tabs.myTeam')}
                  </TabsTrigger>
                </TabsList>
                {mainTab === 'dashboard' && (
                <div className="flex items-center gap-3">
                <Select value={reporteeFilter} onValueChange={setReporteeFilter}>
                  <SelectTrigger className="w-[200px] text-foreground border-border">
                    <SelectValue placeholder={t('learner.team.dashboard.reporteeFilter')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('learner.team.dashboard.reportees.all')}</SelectItem>
                    <SelectItem value="direct">{t('learner.team.dashboard.reportees.direct')}</SelectItem>
                    <SelectItem value="indirect">{t('learner.team.dashboard.reportees.indirect')}</SelectItem>
                    <SelectItem value="managers">{t('learner.team.dashboard.reportees.managers')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-[150px] text-foreground border-border">
                    <SelectValue placeholder={t('learner.team.dashboard.timePeriod')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">{t('learner.team.dashboard.periods.last7')}</SelectItem>
                    <SelectItem value="30">{t('learner.team.dashboard.periods.last30')}</SelectItem>
                    <SelectItem value="90">{t('learner.team.dashboard.periods.last90')}</SelectItem>
                    <SelectItem value="all">{t('learner.team.dashboard.periods.all')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                )}
            </div>

            {/* Dashboard Tab Content */}
            <TabsContent value="dashboard" className="m-0 space-y-6">
            {/* Manager Profile & Team Overview Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Manager Profile Card */}
              {showManagerProfile && (
              <Card>
                <CardHeader className="pb-6">
                  <CardTitle className="text-lg font-semibold">{t('learner.team.dashboard.myProfile')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar name={managerProfile.name} avatar={managerProfile.avatar} className="h-20 w-20" />
                    <div>
                      <h3 className="font-semibold text-lg">{managerProfile.name}</h3>
                      <p className="text-sm text-muted-foreground">{managerProfile.email}</p>
                      {showDesignation && managerProfile.designation && (
                      <Badge variant="outline" className="mt-1">
                        {managerProfile.designation}
                      </Badge>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    {showLeaderboardRank && (
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-center gap-1 page-heading">
                        <Trophy className="h-5 w-5 text-muted-foreground" />{managerProfile.rank >= 1 ? `#${managerProfile.rank}` : '—'}
                      </div>
                      <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.leaderboardRank')}</p>
                    </div>
                    )}
                    {showTotalPoints && (
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <div className="page-heading text-foreground">{managerProfile.totalPoints}</div>
                      <p className="text-xs text-muted-foreground">{t('learner.team.common.totalPoints')}</p>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Team Structure Card */}
              {showTeamStructure && (
              <Card>
                <CardHeader className="pb-6 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold">{t('learner.team.dashboard.teamStructure')}</CardTitle>
                  {showHierarchyButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsHierarchyModalOpen(true)}
                    className="gap-2"
                  >
                    <Network className="h-4 w-4" />
                    {t('learner.team.dashboard.structure')}
                  </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div
                      onClick={() => handleOpenEmployeeList('all')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <Users className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.total}</div>
                        <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.totalReportees')}</p>
                      </div>
                    </div>
                    {showDirectReports && (
                    <div
                      onClick={() => handleOpenEmployeeList('direct')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <UserCheck className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.direct}</div>
                        <p className="text-xs text-muted-foreground">{t('learner.team.common.directReports')}</p>
                      </div>
                    </div>
                    )}
                    {showManagersUnder && (
                    <div
                      onClick={() => handleOpenEmployeeList('managers')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <UserCog className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.managers}</div>
                        <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.managersUnder')}</p>
                      </div>
                    </div>
                    )}
                    {showIndirectReports && (
                    <div
                      onClick={() => handleOpenEmployeeList('indirect')}
                      className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 border border-transparent hover:border-primary/20"
                    >
                      <Users className="h-8 w-8 text-muted-foreground" />
                      <div>
                        <div className="page-heading text-foreground">{stats.indirect}</div>
                        <p className="text-xs text-muted-foreground">{t('learner.team.common.indirectReports')}</p>
                      </div>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}
            </div>

            {/* Phishing Campaign Performance Section */}
            {showPhishingPerformance && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    {t('learner.team.dashboard.phishingPerformance')}
                  </CardTitle>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setPhishingDrawerOpen(true)}
                    >
                      <Filter className="h-4 w-4" />
                      {t('learner.team.common.filterCampaigns')}
                      {selectedPhishingCampaigns.length < phishingCampaigns.length && (
                        <Badge variant="secondary" className="ms-1 px-1.5 py-0">
                          {selectedPhishingCampaigns.length}
                        </Badge>
                      )}
                    </Button>
                    <AdvancedFilterDrawer
                      open={phishingDrawerOpen}
                      onOpenChange={setPhishingDrawerOpen}
                      entityType="campaign"
                      entityLabel={t('learner.team.entities.campaigns')}
                      entityLabelSingular={t('learner.team.entities.campaign')}
                      items={phishingCampaigns as unknown as FilterableEntity[]}
                      selectedIds={selectedPhishingCampaigns}
                      onApply={setSelectedPhishingCampaigns}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* KPI Cards Row - S-O-C-C-R-T-E */}
                {showPhishingKPIs && (
                <div className="grid grid-cols-7 gap-3">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-foreground flex items-center justify-center">
                        <span className="text-lg font-bold text-background">S</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.sent}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.metrics.sent')}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">O</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.opened}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.rateOpened', { rate: filteredPhishingMetrics.openedRate })}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">C</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.clicked}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.rateClicked', { rate: filteredPhishingMetrics.clickedRate })}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">C</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.compromised}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.rateCompromised', { rate: filteredPhishingMetrics.compromisedRate })}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">R</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.reported}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.rateReported', { rate: filteredPhishingMetrics.reportedRate })}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">T</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.trained}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.rateTrained', { rate: filteredPhishingMetrics.trainedRate })}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center">
                        <span className="text-lg font-bold text-white">E</span>
                      </div>
                    </div>
                    <div className="page-heading mb-1 text-foreground">{filteredPhishingMetrics.evaluated}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.rateEvaluated', { rate: filteredPhishingMetrics.evaluatedRate })}</p>
                  </div>
                </div>
                )}

                {/* Campaign Funnel - Full Width */}
                {showPhishingFunnel && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">{t('learner.team.dashboard.campaignFunnel')}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={filteredFunnelData.filter(item => visibleBars.includes(item.key))}
                      layout="horizontal"
                      margin={isRtl ? { top: 10, right: 20, left: 30, bottom: 5 } : { top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        reversed={isRtl}
                        dataKey="name"
                        tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                        axisLine={{ stroke: 'var(--border)' }}
                      />
                      <YAxis orientation={isRtl ? 'right' : 'left'} tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} axisLine={{ stroke: 'var(--border)' }} />
                      <RechartsTooltip
                        formatter={(value, name, props) => [value, props.payload.fullName]}
                        contentStyle={{ fontSize: '12px', borderRadius: '6px', backgroundColor: 'var(--card)', color: 'var(--card-foreground)', border: '1px solid var(--border)' }}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {filteredFunnelData.filter(item => visibleBars.includes(item.key)).map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Custom Interactive Legend — every stage the chart can draw
                      (filteredFunnelData carries the key, colour and translated
                      name; the API's funnelData rows carry none of them, so a
                      legend built from those had no label or colour). */}
                  <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                    {filteredFunnelData.map((item) => (
                      <div
                        key={item.key}
                        onClick={() => handleLegendClick(item.key)}
                        className={`flex items-center gap-2 cursor-pointer transition-opacity ${
                          visibleBars.includes(item.key) ? 'opacity-100' : 'opacity-40'
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-xs font-medium text-foreground">
                          {item.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Performance - Top performers */}
                {showPhishingBreakdown && (
                <div className="space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Column 1: Top Performers */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">{t('learner.team.common.topPerformers')}</h4>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                              <span className="text-xs text-muted-foreground font-semibold">i</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs">{t('learner.team.dashboard.topPerformersHint')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>

                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {topPerformers.length > 0 ? (
                          topPerformers.map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <EmployeeAvatar name={member.name} avatar={member.avatar} className="h-8 w-8 text-xs" />
                                <span className="text-sm font-medium truncate">{member.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>
                                  {richText(t('learner.team.dashboard.reportRateValue'), {
                                    value: <span className="font-medium text-green-600">{member.reportRate}%</span>,
                                  })}
                                </span>
                                <span>
                                  {richText(t('learner.team.dashboard.compromisedValue'), {
                                    value: <span className="font-medium text-green-600">{member.timesCompromised || 0}</span>,
                                  })}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">{t('learner.team.dashboard.noTopPerformers')}</p>
                        )}
                      </div>
                    </div>

                    {/* Column 2: Repeat Offenders */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">{t('learner.team.dashboard.repeatOffenders')}</h4>
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                                <span className="text-xs text-muted-foreground font-semibold">i</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{t('learner.team.dashboard.repeatOffendersHint')}</p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge variant="outline" className="text-xs">{repeatOffenders.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {repeatOffenders.length > 0 ? (
                          repeatOffenders.slice(0, 5).map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <EmployeeAvatar name={member.name} avatar={member.avatar} className="h-8 w-8 text-xs" />
                                <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                                <Badge variant="destructive" className="text-xs px-1.5 py-0">
                                  {t('learner.team.common.times', { count: member.timesCompromised ?? 0 })}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>
                                  {richText(t('learner.team.dashboard.compromisedTimes'), {
                                    value: (
                                      <span className="font-medium text-red-600">
                                        {t('learner.team.common.times', { count: member.timesCompromised ?? 0 })}
                                      </span>
                                    ),
                                  })}
                                </span>
                                <span>
                                  {richText(t('learner.team.dashboard.trainingValue'), {
                                    value: <span className="font-medium">{member.trainingCompletion}%</span>,
                                  })}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">{t('learner.team.dashboard.noRepeatOffenders')}</p>
                        )}
                      </div>
                    </div>

                    {/* Column 3: Vulnerable */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">{t('learner.team.dashboard.vulnerable')}</h4>
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                                <span className="text-xs text-muted-foreground font-semibold">i</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{t('learner.team.dashboard.vulnerableHint')}</p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge variant="outline" className="text-xs">{vulnerableMembers.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {vulnerableMembers.length > 0 ? (
                          vulnerableMembers.slice(0, 5).map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-xs font-semibold`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>
                                  {richText(t('learner.team.dashboard.reportRateLabel'), {
                                    value: <span className="font-medium text-orange-600">{member.reportRate}%</span>,
                                  })}
                                </span>
                                <span>
                                  {richText(t('learner.team.dashboard.compromisedLabel'), {
                                    value: (
                                      <span className="font-medium">
                                        {t('learner.team.common.times', { count: member.timesCompromised || 0 })}
                                      </span>
                                    ),
                                  })}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">{t('learner.team.dashboard.noVulnerable')}</p>
                        )}
                      </div>
                    </div>

                    {/* Column 4: Laggards */}
                    <div className="space-y-3 bg-muted/30 p-4 rounded-lg border border-border max-h-[320px] flex flex-col">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">{t('learner.team.dashboard.laggards')}</h4>
                        <div className="flex items-center gap-1.5">
                          <Tooltip>
                            <TooltipTrigger>
                              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center cursor-help">
                                <span className="text-xs text-muted-foreground font-semibold">i</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{t('learner.team.dashboard.laggardsHint')}</p>
                            </TooltipContent>
                          </Tooltip>
                          <Badge variant="outline" className="text-xs">{laggardMembers.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 overflow-y-auto flex-1 min-h-0">
                        {laggardMembers.length > 0 ? (
                          laggardMembers.slice(0, 5).map((member) => (
                            <div
                              key={member.id}
                              className="p-2.5 bg-card rounded-lg space-y-1"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-xs font-semibold`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium truncate flex-1">{member.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span>
                                  {richText(t('learner.team.dashboard.trainingValue'), {
                                    value: <span className="font-medium text-amber-600">{member.trainingCompletion}%</span>,
                                  })}
                                </span>
                                <span>
                                  {richText(t('learner.team.dashboard.overdueValue'), {
                                    value: <span className="font-medium text-red-600">{member.overdueRate}%</span>,
                                  })}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-muted-foreground text-center py-8">{t('learner.team.dashboard.noLaggards')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Training Performance Section */}
            {showTrainingPerformance && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    {t('learner.team.dashboard.trainingPerformance')}
                  </CardTitle>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setTrainingDrawerOpen(true)}
                    >
                      <Filter className="h-4 w-4" />
                      {t('learner.team.common.filterTraining')}
                      {selectedTrainingCourses.length < trainingCourses.length && (
                        <Badge variant="secondary" className="ms-1 px-1.5 py-0">
                          {selectedTrainingCourses.length}
                        </Badge>
                      )}
                    </Button>
                    <AdvancedFilterDrawer
                      open={trainingDrawerOpen}
                      onOpenChange={setTrainingDrawerOpen}
                      entityType="training"
                      entityLabel={t('learner.team.entities.trainingCourses')}
                      entityLabelSingular={t('learner.team.entities.trainingCourse')}
                      items={trainingCourses as unknown as FilterableEntity[]}
                      selectedIds={selectedTrainingCourses}
                      onApply={setSelectedTrainingCourses}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* KPI Cards Row */}
                {showTrainingKPIs && (
                <div className="grid grid-cols-5 gap-3">
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.assigned}</div>
                    <p className="text-xs font-medium text-muted-foreground">{t('learner.team.dashboard.totalAssigned')}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.completed} <span className="text-sm font-normal">({filteredTrainingMetrics.completionRate}%)</span></div>
                    <p className="text-xs font-medium text-muted-foreground">{t('learner.team.metrics.completed')}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.notStarted}</div>
                    <p className="text-xs font-medium text-muted-foreground">{t('learner.team.metrics.notStarted')}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.inProgress}</div>
                    <p className="text-xs font-medium text-muted-foreground">{t('learner.team.metrics.inProgress')}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-xl text-center border border-border">
                    <div className="page-heading text-foreground">{filteredTrainingMetrics.overdue}</div>
                    <p className="text-xs font-medium text-muted-foreground">{t('learner.team.metrics.overdue')}</p>
                  </div>
                </div>
                )}

                {/* Training Completion by Member */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Top Completers */}
                  {showTopCompleters && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">
                      {t('learner.team.dashboard.topCompleters')}
                    </h4>
                    <div className="space-y-2">
                      {[...filteredMembers]
                        .sort((a, b) => (b.trainingCompletion || 0) - (a.trainingCompletion || 0))
                        .slice(0, 3)
                        .map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">{member.name}</p>
                                <p className="text-xs text-muted-foreground">{member.department}</p>
                              </div>
                            </div>
                            <div className="text-end">
                              <p className="text-lg font-bold text-green-600">{member.trainingCompletion || 0}%</p>
                              <p className="text-xs text-muted-foreground">{t('learner.team.metrics.completed')}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  )}

                  {/* Needs Improvement */}
                  {showNeedsImprovement && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">
                      {t('learner.team.dashboard.needsImprovement')}
                    </h4>
                    <div className="space-y-2">
                      {[...filteredMembers]
                        .sort((a, b) => (a.trainingCompletion || 0) - (b.trainingCompletion || 0))
                        .slice(0, 3)
                        .map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">{member.name}</p>
                                <p className="text-xs text-muted-foreground">{member.department}</p>
                              </div>
                            </div>
                            <div className="text-end">
                              <p className="text-lg font-bold text-red-600">{member.trainingCompletion || 0}%</p>
                              <p className="text-xs text-muted-foreground">{t('learner.team.metrics.completed')}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Announcement Campaign Performance Section */}
            {showAnnouncementPerformance && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">
                    {t('learner.team.dashboard.announcementPerformance')}
                  </CardTitle>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setAnnouncementDrawerOpen(true)}
                    >
                      <Filter className="h-4 w-4" />
                      {t('learner.team.common.filterAnnouncements')}
                      {selectedAnnouncementCampaigns.length < announcementCampaigns.length && (
                        <Badge variant="secondary" className="ms-1 px-1.5 py-0">
                          {selectedAnnouncementCampaigns.length}
                        </Badge>
                      )}
                    </Button>
                    <AdvancedFilterDrawer
                      open={announcementDrawerOpen}
                      onOpenChange={setAnnouncementDrawerOpen}
                      entityType="announcement"
                      entityLabel={t('learner.team.entities.announcements')}
                      entityLabelSingular={t('learner.team.entities.announcement')}
                      items={announcementCampaigns as unknown as FilterableEntity[]}
                      selectedIds={selectedAnnouncementCampaigns}
                      onApply={setSelectedAnnouncementCampaigns}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* KPI Cards Row */}
                {showAnnouncementKPIs && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.sent}</div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.announcementsSent')}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.opened} <span className="text-sm font-normal">({filteredAnnouncementMetrics.openedRate}%)</span></div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.metrics.opened')}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.acknowledged} <span className="text-sm font-normal">({filteredAnnouncementMetrics.acknowledgedRate}%)</span></div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.metrics.acknowledged')}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <div className="page-heading text-foreground">{filteredAnnouncementMetrics.trained} <span className="text-sm font-normal">({filteredAnnouncementMetrics.trainedRate}%)</span></div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.trainingComplete')}</p>
                  </div>
                </div>
                )}

                {/* Announcement Funnel Chart */}
                {showAnnouncementFunnel && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">{t('learner.team.dashboard.announcementFunnel')}</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={announcementFunnelData.filter(item => visibleAnnouncementBars.includes(item.key))}
                      layout="horizontal"
                      margin={isRtl ? { top: 10, right: 20, left: 30, bottom: 5 } : { top: 10, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        reversed={isRtl}
                        dataKey="name"
                        tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                        axisLine={{ stroke: 'var(--border)' }}
                      />
                      <YAxis orientation={isRtl ? 'right' : 'left'} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={{ stroke: 'var(--border)' }} />
                      <RechartsTooltip
                        formatter={(value, name, props) => [value, props.payload.fullName]}
                        contentStyle={{ fontSize: '12px', borderRadius: '6px', backgroundColor: 'var(--card)', color: 'var(--card-foreground)', border: '1px solid var(--border)' }}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {announcementFunnelData.filter(item => visibleAnnouncementBars.includes(item.key)).map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.fill}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Custom Interactive Legend */}
                  <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                    {announcementFunnelData.map((item) => (
                      <div
                        key={item.key}
                        onClick={() => handleAnnouncementLegendClick(item.key)}
                        className={`flex items-center gap-2 cursor-pointer transition-opacity ${
                          visibleAnnouncementBars.includes(item.key) ? 'opacity-100' : 'opacity-40'
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-xs font-medium text-foreground">
                          {item.fullName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Gamification Overview - Consolidated Section */}
            {showGamification && (
            <Card>
              <CardHeader className="pb-6">
                <CardTitle className="text-lg font-semibold">
                  {t('learner.team.dashboard.gamificationOverview')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Levels Section — one tile per rung of the workspace's ladder */}
                {showLevels && (levelLadder.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('learner.team.levels.noneSetUp')}
                  </p>
                ) : (
                <div className="grid grid-cols-5 gap-3">
                  {levelLadder.map((level, index) => {
                    const employeesAtLevel = filteredMembers.filter(m => levelOf(m.totalPoints)?.id === level.id).length;
                    const levelColors = [
                      { bg: 'bg-muted/50', border: 'border-border', text: 'text-foreground', hover: 'hover:border-border' },
                      { bg: 'bg-yellow-50 dark:bg-yellow-950', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300', hover: 'hover:border-yellow-300 dark:hover:border-yellow-600' },
                      { bg: 'bg-orange-50 dark:bg-orange-950', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300', hover: 'hover:border-orange-300 dark:hover:border-orange-600' },
                      { bg: 'bg-teal-50 dark:bg-teal-950', border: 'border-teal-200 dark:border-teal-800', text: 'text-teal-700 dark:text-teal-300', hover: 'hover:border-teal-300 dark:hover:border-teal-600' },
                      { bg: 'bg-purple-50 dark:bg-purple-950', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-700 dark:text-purple-300', hover: 'hover:border-purple-300 dark:hover:border-purple-600' },
                    ];
                    const colors = levelColors[index % levelColors.length];
                    return (
                      <div
                        key={level.id}
                        data-testid="level-tile"
                        onClick={() => setSelectedLevel(level.id)}
                        className={`p-4 ${colors.bg} rounded-xl border ${colors.border} ${colors.hover} text-center space-y-3 cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all`}
                      >
                        <div className="flex justify-center">
                          {/* imageUrl is an uploaded image or, for the global
                              seed, a Lucide icon name — BadgeIcon renders both. */}
                          <div className={`relative w-14 h-14 flex items-center justify-center ${colors.text}`}>
                            <BadgeIcon name={level.imageUrl} alt={level.name} className="w-14 h-14" />
                          </div>
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${colors.text}`}>{level.name}</p>
                          <p className="text-xs text-muted-foreground">{rungBandText(index)}</p>
                        </div>
                        <p className={`page-heading ${colors.text}`}>
                          {employeesAtLevel}
                        </p>
                        <p className="text-xs text-muted-foreground">{t('learner.team.dashboard.employeesNoun', { count: employeesAtLevel })}</p>
                      </div>
                    );
                  })}
                </div>
                ))}

                {/* Badge Distribution Section */}
                {showBadgeDistribution && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">{t('learner.team.dashboard.badgeDistribution')}</h3>
                  <div className="grid grid-cols-3 gap-3 max-h-[320px] overflow-y-auto">
                    {badgeDistributionData.map((badge, index) => {
                      const totalEmployees = filteredMembers.length;
                      const percentage = Math.round((badge.count / totalEmployees) * 100);
                      const hasEmployees = badge.count > 0;
                      return (
                        <div
                          key={badge.badgeId || index}
                          onClick={() => setSelectedBadge({ name: badge.badgeName, icon: badge.badgeIcon, count: badge.count })}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            hasEmployees
                              ? 'bg-muted/20 border-border hover:shadow-md hover:border-primary/30'
                              : 'bg-muted/5 border-border/50 opacity-60'
                          }`}
                        >
                          <BadgePicture
                            src={badge.badgeIcon}
                            name={badge.badgeName}
                            className={`w-9 h-9 object-contain ${!hasEmployees ? 'grayscale' : ''}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${!hasEmployees ? 'text-muted-foreground' : ''}`}>
                              <UserText truncate>{badge.badgeName}</UserText>
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={percentage} className="h-1.5 flex-1" />
                              <span className={`text-xs whitespace-nowrap ${!hasEmployees ? 'text-muted-foreground/70' : 'text-muted-foreground'}`}>
                                {badge.count}/{totalEmployees} ({percentage}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Team Leaderboard Section */}
                {showTeamLeaderboard && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-foreground">{t('learner.team.dashboard.teamLeaderboard')}</h3>
                  {/* Header Row */}
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <span className="text-xs font-medium text-muted-foreground w-8">{t('learner.team.common.rank')}</span>
                      <span className="text-xs font-medium text-muted-foreground">{t('learner.team.common.employee')}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-medium text-muted-foreground w-36 text-center">{t('learner.team.common.level')}</span>
                      <span className="text-xs font-medium text-muted-foreground w-32 text-center">{t('learner.team.common.points')}</span>
                      <span className="text-xs font-medium text-muted-foreground w-32 text-center">{t('learner.team.common.badges')}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[...filteredMembers]
                      .sort((a, b) => a.rank - b.rank)
                      .slice(0, 10)
                      .map((member) => {
                        const memberLevel = levelOf(member.totalPoints);
                        return (
                          <div
                            key={member.id}
                            onClick={() => setSelectedLeaderboardEmployee(member)}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 hover:border-primary/30 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-sm font-bold w-8">#{member.rank}</span>
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                  {getInitials(member.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <span className="text-sm font-medium">{member.name}</span>
                                <p className="text-xs text-muted-foreground">{member.department}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {/* Level Column - Wider Width */}
                              <div className="w-36 flex justify-center">
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-lg border border-border">
                                  {memberLevel ? (
                                    <>
                                      <span className="relative w-6 h-6 flex items-center justify-center">
                                        <BadgeIcon name={memberLevel.imageUrl} alt={memberLevel.name} className="w-6 h-6" />
                                      </span>
                                      <span className="text-xs font-medium">{memberLevel.name}</span>
                                    </>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">{t('learner.team.levels.noLevelYet')}</span>
                                  )}
                                </div>
                              </div>
                              {/* Points Column - Wider Width */}
                              <div className="w-32 flex justify-center">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg border border-border">
                                  <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  <span className="text-xs font-semibold">{member.totalPoints}</span>
                                </div>
                              </div>
                              {/* Badges Column - Wider Width */}
                              <div className="w-32 flex justify-center">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg border border-border">
                                  <div className="flex -space-x-1">
                                    {badgeImages.slice(0, Math.min(member.badgeCount, 3)).map((src, i) => (
                                      <img 
                                        key={i} 
                                        src={src} 
                                        alt={t('learner.team.common.badgeAlt', { n: i + 1 })} 
                                        className="w-5 h-5 rounded-full ring-1 ring-white object-cover" 
                                      />
                                    ))}
                                  </div>
                                  <span className="text-xs font-medium">{member.badgeCount}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Level Employees Modal */}
            <Dialog open={selectedLevel !== null} onOpenChange={(open) => !open && setSelectedLevel(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {(() => {
                      const rung = levelLadder.find(l => l.id === selectedLevel);
                      return rung ? (
                        <>
                          <span className="relative w-8 h-8 flex items-center justify-center">
                            <BadgeIcon name={rung.imageUrl} alt={rung.name} className="w-8 h-8" />
                          </span>
                          {t('learner.team.dialogs.levelEmployees', { level: rung.name })}
                        </>
                      ) : null;
                    })()}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {t('learner.team.dialogs.levelEmployeesDescription')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedLevel && filteredMembers
                    .filter(m => levelOf(m.totalPoints)?.id === selectedLevel)
                    .map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.department}</p>
                        </div>
                        <div className="text-end">
                          <p className="text-sm font-semibold">{t('learner.team.common.pts', { points: member.totalPoints })}</p>
                          <p className="text-xs text-muted-foreground">{t('learner.team.common.rankNumber', { rank: member.rank })}</p>
                        </div>
                      </div>
                    ))}
                  {selectedLevel && filteredMembers.filter(m => levelOf(m.totalPoints)?.id === selectedLevel).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">{t('learner.team.dialogs.noEmployeesAtLevel')}</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Badge Employees Modal */}
            <Dialog open={selectedBadge !== null} onOpenChange={(open) => !open && setSelectedBadge(null)}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {selectedBadge && (
                      <>
                        <BadgePicture
                          src={selectedBadge.icon}
                          name={selectedBadge.name}
                          className="w-8 h-8 object-contain"
                        />
                        <UserText>{selectedBadge.name}</UserText>
                      </>
                    )}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {t('learner.team.dialogs.badgeEmployeesDescription')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {selectedBadge && filteredMembers
                    .slice(0, selectedBadge.count)
                    .map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.department}</p>
                        </div>
                        <div className="text-end">
                          <p className="text-sm font-semibold">{t('learner.team.common.pts', { points: member.totalPoints })}</p>
                          <p className="text-xs text-muted-foreground">{t('learner.team.common.rankNumber', { rank: member.rank })}</p>
                        </div>
                      </div>
                    ))}
                  {selectedBadge && selectedBadge.count === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">{t('learner.team.dialogs.noEmployeesWithBadge')}</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Team Members Risk Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  {showSafest ? t('learner.team.riskTable.lowTitle') : t('learner.team.riskTable.highTitle')}
                  <Badge variant={showSafest ? 'secondary' : 'destructive'} className="ms-2">
                    {filteredRiskMembers.length}
                  </Badge>
                </h2>
                <div className="flex items-center gap-2">
                  <Label htmlFor="risk-toggle" className="text-sm text-muted-foreground">
                    {t('learner.team.risk.highRisk')}
                  </Label>
                  <Switch
                    id="risk-toggle"
                    checked={showSafest}
                    onCheckedChange={(checked) => {
                      setShowSafest(checked);
                      setRiskTablePage(0);
                      setRiskLevelFilter('all');
                    }}
                  />
                  <Label htmlFor="risk-toggle" className="text-sm text-muted-foreground">
                    {t('learner.team.risk.lowRisk')}
                  </Label>
                </div>
              </div>

              {/* Filter Bar */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('learner.team.common.searchNameEmail')}
                    value={riskTableSearch}
                    onChange={(e) => {
                      setRiskTableSearch(e.target.value);
                      setRiskTablePage(0);
                    }}
                    className="ps-9 h-9 text-sm"
                  />
                </div>
                
                {/* Department Filter */}
                <Select 
                  value={departmentFilter} 
                  onValueChange={(value) => {
                    setDepartmentFilter(value);
                    setRiskTablePage(0);
                  }}
                >
                  <SelectTrigger className="w-[160px] h-9 text-sm text-foreground border-border">
                    <SelectValue placeholder={t('learner.team.common.department')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('learner.team.common.allDepartments')}</SelectItem>
                    {allDepartments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Clear Filters */}
                {(riskTableSearch || departmentFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRiskTableSearch('');
                      setDepartmentFilter('all');
                      setRiskTablePage(0);
                    }}
                    className="h-9 text-sm text-muted-foreground"
                  >
                    {t('learner.team.riskTable.clearFilters')}
                  </Button>
                )}
              </div>

              {/* Table */}
              {paginatedRiskMembers.length > 0 ? (
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => handleRiskTableSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            {t('learner.team.common.employee')}
                            <ArrowUpDown className={`h-3.5 w-3.5 ${riskTableSort.key === 'name' ? 'text-foreground' : 'text-muted-foreground'}`} />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => handleRiskTableSort('riskLevel')}
                        >
                          <div className="flex items-center gap-1">
                            {t('learner.team.riskTable.riskLevel')}
                            <ArrowUpDown className={`h-3.5 w-3.5 ${riskTableSort.key === 'riskLevel' ? 'text-foreground' : 'text-muted-foreground'}`} />
                          </div>
                        </TableHead>
                        <TableHead>{t('learner.team.riskTable.phishingPerformance')}</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/80 transition-colors"
                          onClick={() => handleRiskTableSort('trainingCompletion')}
                        >
                          <div className="flex items-center gap-1">
                            {t('learner.team.riskTable.trainingStatus')}
                            <ArrowUpDown className={`h-3.5 w-3.5 ${riskTableSort.key === 'trainingCompletion' ? 'text-foreground' : 'text-muted-foreground'}`} />
                          </div>
                        </TableHead>
                        <TableHead>{t('learner.team.riskTable.riskFactors')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedRiskMembers.map((member) => {
                        const riskFactors = getRiskFactors(t, member);
                        const trainingCompletion = member.trainingCompletion || 0;
                        const overdueRate = member.overdueRate || 0;
                        const reportRate = member.reportRate || 0;
                        const timesCompromised = member.timesCompromised || 0;
                        const visibleFactors = riskFactors.slice(0, 2);
                        const remainingFactors = riskFactors.length - 2;
                        
                        return (
                          <TableRow key={member.id} className="hover:bg-muted/30">
                            {/* Employee */}
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarFallback className={`${getAvatarColor(member.name)} text-white text-sm font-semibold`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="space-y-1">
                                  <p className="font-medium text-sm text-foreground">{member.name}</p>
                                  <p className="text-xs text-muted-foreground">{member.email}</p>
                                  <Badge variant="outline" className="text-xs text-foreground">{member.department}</Badge>
                                </div>
                              </div>
                            </TableCell>
                            
                            {/* Risk Level */}
                            <TableCell>
                              <Badge className={getRiskLevelColor(member.riskLevel)}>
                                {riskLevelText(t, member.riskLevel)}
                              </Badge>
                            </TableCell>
                            
                            {/* Phishing Performance */}
                            <TableCell>
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">{t('learner.team.riskTable.compromised')}</span>
                                  <span className={`text-sm font-semibold ${timesCompromised === 0 ? 'text-green-600 dark:text-green-400' : timesCompromised <= 1 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {t('learner.team.common.times', { count: timesCompromised })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">{t('learner.team.riskTable.reportRate')}</span>
                                  <span className={`text-sm font-semibold ${reportRate >= 70 ? 'text-green-600 dark:text-green-400' : reportRate >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {reportRate}%
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            
                            {/* Training Status */}
                            <TableCell>
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">{t('learner.team.riskTable.completion')}</span>
                                  <span className={`text-sm font-semibold ${trainingCompletion >= 80 ? 'text-green-600 dark:text-green-400' : trainingCompletion >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {trainingCompletion}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground w-20">{t('learner.team.riskTable.overdue')}</span>
                                  <span className={`text-sm font-semibold ${overdueRate <= 10 ? 'text-green-600 dark:text-green-400' : overdueRate <= 30 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {overdueRate}%
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                            
                            {/* Risk Factors */}
                            <TableCell>
                              <div className="flex flex-wrap items-center gap-1.5">
                                {riskFactors.length > 0 ? (
                                  <>
                                    {visibleFactors.map((factor, idx) => (
                                      <Badge 
                                        key={idx} 
                                        variant="outline"
                                        className={`text-xs ${factor.colorClass}`}
                                      >
                                        {factor.label}
                                      </Badge>
                                    ))}
                                    {remainingFactors > 0 && (
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Badge variant="outline" className="text-xs bg-muted">
                                            {t('learner.team.riskTable.more', { count: remainingFactors })}
                                          </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <div className="flex flex-col gap-1">
                                            {riskFactors.slice(2).map((factor, idx) => (
                                              <span key={idx} className="text-xs">{factor.label}</span>
                                            ))}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-xs text-green-600 font-medium">{t('learner.team.riskTable.noIssues')}</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground border rounded-lg">
                  <p className="text-sm">{showSafest ? t('learner.team.riskTable.noneLow') : t('learner.team.riskTable.noneHigh')}</p>
                  {(riskTableSearch || departmentFilter !== 'all') && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => {
                        setRiskTableSearch('');
                        setDepartmentFilter('all');
                      }}
                      className="mt-2"
                    >
                      {t('learner.team.riskTable.clearAllFilters')}
                    </Button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {filteredRiskMembers.length > 0 && (
                <div className="flex items-center justify-between pt-2">
                  <div className="text-sm text-muted-foreground">
                    {t('learner.team.riskTable.showing', {
                      from: riskTablePage * riskTablePageSize + 1,
                      to: Math.min((riskTablePage + 1) * riskTablePageSize, filteredRiskMembers.length),
                      total: filteredRiskMembers.length,
                    })}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Page Size Selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('learner.team.riskTable.show')}</span>
                      <Select
                        value={`${riskTablePageSize}`}
                        onValueChange={(value) => {
                          setRiskTablePageSize(parseInt(value));
                          setRiskTablePage(0);
                        }}
                      >
                        <SelectTrigger className="w-[70px] h-8 text-sm text-foreground border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[5, 10, 20, 50].map(size => (
                            <SelectItem key={size} value={`${size}`}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Page Navigation */}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('learner.team.common.previousPage')}
                        className="h-8 w-8"
                        onClick={() => setRiskTablePage(p => Math.max(0, p - 1))}
                        disabled={riskTablePage === 0}
                      >
                        <ChevronLeft className="h-4 w-4 rtl-flip" />
                      </Button>
                      <div className="flex items-center gap-1 px-2 text-sm text-muted-foreground">
                        {richText(t('learner.team.common.pageOf'), {
                          page: <span className="font-medium text-foreground">{riskTablePage + 1}</span>,
                          pages: <span className="font-medium text-foreground">{riskTablePageCount || 1}</span>,
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('learner.team.common.nextPage')}
                        className="h-8 w-8"
                        onClick={() => setRiskTablePage(p => Math.min(riskTablePageCount - 1, p + 1))}
                        disabled={riskTablePage >= riskTablePageCount - 1}
                      >
                        <ChevronRight className="h-4 w-4 rtl-flip" />
                      </Button>
                    </div>
                    
                    {/* Go to Page */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('learner.team.riskTable.goTo')}</span>
                      <Input
                        type="number"
                        min={1}
                        max={riskTablePageCount}
                        className="h-8 w-14 text-center text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const value = parseInt((e.target as HTMLInputElement).value);
                            if (value >= 1 && value <= riskTablePageCount) {
                              setRiskTablePage(value - 1);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Weekly recap launcher (ticket 846) — a persistent card so
                admins in view-as (previewMode) and managers can always open the
                recap on demand, rather than relying on the once-per-week
                auto-open effect that is intentionally suppressed in previewMode. */}
            {hasWeeklyRecap && (
              <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
                <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{t('learner.team.recap.title')}</h3>
                      <p className="text-sm text-muted-foreground">
                        {/* What the recap measures, no more: with Gamification
                            off it promises no points (gamification-3 item 1). */}
                        {t(
                          gamificationOn
                            ? 'learner.team.recap.launcherGamification'
                            : 'learner.team.recap.launcherPhishing',
                          { period: recapPeriod || t('learner.team.recap.lastWeek') },
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    className="shrink-0"
                    onClick={() => {
                      setRecapSlide(0);
                      setShowWeeklyRecap(true);
                    }}
                  >
                    <PartyPopper className="me-2 h-4 w-4" />
                    {t('learner.team.recap.view')}
                  </Button>
                </CardContent>
              </Card>
            )}
            </TabsContent>

            {/* My Team Tab Content */}
            <TabsContent value="myteam" className="m-0 space-y-6">
              {/* Manager Info Bar */}
              <div className="flex items-center justify-between py-4 px-6 bg-muted/30 rounded-lg border">
                <div className="flex items-center gap-8">
                  {/* Back Button when viewing as someone else */}
                  {viewAsManager && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('learner.team.myTeam.back')}
                      onClick={handleBackToMyTeam}
                      className="shrink-0 h-8 w-8"
                    >
                      <ChevronLeft className="h-5 w-5 rtl-flip" />
                    </Button>
                  )}

                  {/* Manager Avatar and Info */}
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={myTeamCurrentManager.avatar} alt={myTeamCurrentManager.name} />
                      <AvatarFallback>
                        {myTeamCurrentManager.name.split(' ').map((n: string) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground">{myTeamCurrentManager.name}</p>
                        {viewAsManager && (
                          <Badge variant="secondary" className="text-xs">{t('learner.team.myTeam.viewingAs')}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                    </div>
                  </div>
                  
                  {/* Designation */}
                  <div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.common.designation')}</p>
                    <p className="text-sm text-foreground">{myTeamCurrentManager.designation}</p>
                  </div>

                  {/* Reports To */}
                  <div>
                    <p className="text-xs text-muted-foreground">{t('learner.team.myTeam.reportsTo')}</p>
                    <p className="text-sm text-foreground">{getMyTeamCurrentManagersManager().name}</p>
                  </div>
                </div>

                {/* Team Stats on the right - clickable filters */}
                <div className="flex items-center gap-2">
                  {[
                    { key: 'direct' as const, count: myTeamFilterCounts.direct, label: t('learner.team.myTeam.filters.direct') },
                    { key: 'indirect' as const, count: myTeamFilterCounts.indirect, label: t('learner.team.myTeam.filters.indirect') },
                    { key: 'myteam' as const, count: myTeamFilterCounts.myteam, label: t('learner.team.myTeam.filters.managers') },
                    { key: 'all' as const, count: myTeamFilterCounts.all, label: t('learner.team.myTeam.filters.all') },
                  ].map((stat) => (
                    <button
                      key={stat.key}
                      onClick={() => { setMyTeamFilter(stat.key); setMyTeamCurrentPage(1); }}
                      className={`text-center px-4 py-2 rounded-lg transition-all cursor-pointer ${
                        myTeamFilter === stat.key
                          ? 'bg-foreground text-background shadow-sm'
                          : 'hover:bg-muted active:bg-muted/80'
                      }`}
                    >
                      <p className={`text-xl font-bold ${myTeamFilter === stat.key ? 'text-background' : 'text-foreground'}`}>{stat.count}</p>
                      <p className={`text-xs ${myTeamFilter === stat.key ? 'text-background/70' : 'text-muted-foreground'}`}>{stat.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Table Section */}
              <Tabs value={myTeamTab} onValueChange={setMyTeamActiveTab} className="space-y-4">

                {/* Table Card with Tabs */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <TabsList className="h-auto p-0 bg-transparent gap-1 w-fit">
                        {showMyTeamLeaderboard && <TabsTrigger value="leaderboard" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">{t('learner.team.myTeam.tabs.leaderboard')}</TabsTrigger>}
                        {showMyTeamRiskScore && <TabsTrigger value="risk-score" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">{t('learner.team.myTeam.tabs.riskScore')}</TabsTrigger>}
                        {showMyTeamPhishing && <TabsTrigger value="phishing" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">{t('learner.team.myTeam.tabs.phishing')}</TabsTrigger>}
                        {showMyTeamTraining && <TabsTrigger value="training" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">{t('learner.team.myTeam.tabs.training')}</TabsTrigger>}
                        {showMyTeamAnnouncement && <TabsTrigger value="announcement" className="px-3 py-2 rounded-lg text-sm font-medium transition-all data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground">{t('learner.team.myTeam.tabs.announcement')}</TabsTrigger>}
                      </TabsList>
                    </div>
                    {/* Search and Filters - moved inside card */}
                    <div className="flex items-center gap-4 mt-6 pt-3">
                      <div className="relative flex-1 max-w-md">
                        <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder={t('learner.team.myTeam.searchPlaceholder')}
                          value={myTeamSearchQuery}
                          onChange={(e) => { setMyTeamSearchQuery(e.target.value); setMyTeamCurrentPage(1); }}
                          className="ps-10"
                        />
                      </div>
                      <Select
                        value={myTeamDepartmentFilter}
                        onValueChange={(value) => { setMyTeamDepartmentFilter(value); setMyTeamCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-40 text-foreground border-border">
                          <SelectValue placeholder={t('learner.team.common.allDepartments')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('learner.team.common.allDepartments')}</SelectItem>
                          {myTeamDepartments.map((dept) => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={myTeamSort}
                        onValueChange={(value) => { setMyTeamSort(value as 'name' | 'lastActivity' | 'points'); setMyTeamCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-40 text-foreground border-border" aria-label={t('learner.team.common.sortBy')}>
                          <SelectValue placeholder={t('learner.team.common.sortBy')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lastActivity">{t('learner.team.myTeam.sort.lastActivity')}</SelectItem>
                          <SelectItem value="name">{t('learner.team.myTeam.sort.name')}</SelectItem>
                          {gamificationOn && <SelectItem value="points">{t('learner.team.myTeam.sort.points')}</SelectItem>}
                        </SelectContent>
                      </Select>
                      {/* Context-aware filter drawers */}
                      {myTeamActiveTab === 'phishing' && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => setMyTeamPhishingDrawerOpen(true)}
                          >
                            <Filter className="h-4 w-4" />
                            {t('learner.team.common.filterCampaigns')}
                            {myTeamSelectedPhishingCampaigns.length < myTeamPhishingCampaigns.length && (
                              <Badge variant="secondary" className="ms-1 px-1.5 py-0">
                                {myTeamSelectedPhishingCampaigns.length}
                              </Badge>
                            )}
                          </Button>
                          <AdvancedFilterDrawer
                            open={myTeamPhishingDrawerOpen}
                            onOpenChange={setMyTeamPhishingDrawerOpen}
                            entityType="campaign"
                            entityLabel={t('learner.team.entities.campaigns')}
                            entityLabelSingular={t('learner.team.entities.campaign')}
                            items={myTeamPhishingCampaigns as unknown as FilterableEntity[]}
                            selectedIds={myTeamSelectedPhishingCampaigns}
                            onApply={setMyTeamSelectedPhishingCampaigns}
                          />
                        </div>
                      )}
                      {myTeamActiveTab === 'training' && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => setMyTeamTrainingDrawerOpen(true)}
                          >
                            <Filter className="h-4 w-4" />
                            {t('learner.team.common.filterTraining')}
                            {myTeamSelectedTrainingCourses.length < myTeamTrainingCourses.length && (
                              <Badge variant="secondary" className="ms-1 px-1.5 py-0">
                                {myTeamSelectedTrainingCourses.length}
                              </Badge>
                            )}
                          </Button>
                          <AdvancedFilterDrawer
                            open={myTeamTrainingDrawerOpen}
                            onOpenChange={setMyTeamTrainingDrawerOpen}
                            entityType="training"
                            entityLabel={t('learner.team.entities.trainingCourses')}
                            entityLabelSingular={t('learner.team.entities.trainingCourse')}
                            items={myTeamTrainingCourses as unknown as FilterableEntity[]}
                            selectedIds={myTeamSelectedTrainingCourses}
                            onApply={setMyTeamSelectedTrainingCourses}
                          />
                        </div>
                      )}
                      {myTeamActiveTab === 'announcement' && (
                        <div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => setMyTeamAnnouncementDrawerOpen(true)}
                          >
                            <Filter className="h-4 w-4" />
                            {t('learner.team.common.filterAnnouncements')}
                            {myTeamSelectedAnnouncementCampaigns.length < myTeamAnnouncementCampaigns.length && (
                              <Badge variant="secondary" className="ms-1 px-1.5 py-0">
                                {myTeamSelectedAnnouncementCampaigns.length}
                              </Badge>
                            )}
                          </Button>
                          <AdvancedFilterDrawer
                            open={myTeamAnnouncementDrawerOpen}
                            onOpenChange={setMyTeamAnnouncementDrawerOpen}
                            entityType="announcement"
                            entityLabel={t('learner.team.entities.announcements')}
                            entityLabelSingular={t('learner.team.entities.announcement')}
                            items={myTeamAnnouncementCampaigns as unknown as FilterableEntity[]}
                            selectedIds={myTeamSelectedAnnouncementCampaigns}
                            onApply={setMyTeamSelectedAnnouncementCampaigns}
                          />
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  {/* Leaderboard Tab */}
                  {showMyTeamLeaderboard && (
                  <TabsContent value="leaderboard" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:ps-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold ps-4">{t('learner.team.common.employee')}</TableHead>
                              {myTeamFilter === 'myteam' && <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.viewAs')}</TableHead>}
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.membersInTeam')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.common.points')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.rankInTeam')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.rankInDepartment')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.rankInOrg')}</TableHead>
                              <TableHead className="font-semibold">{t('learner.team.common.department')}</TableHead>
                              <TableHead className="font-semibold">{t('learner.team.common.designation')}</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">{t('learner.team.myTeam.columns.hierarchy')}</TableHead>}
                              <TableHead className="font-semibold">{t('learner.team.myTeam.columns.manager')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openTeamMember(member)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                {myTeamFilter === 'myteam' && (
                                  <TableCell className="text-center">
                                    {member.isManager && member.membersInTeam > 0 ? (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button variant="ghost" size="icon" aria-label={t('learner.team.myTeam.viewAsName', { name: member.name })} className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleViewAs(member); }}>
                                              <Eye className="h-4 w-4 text-muted-foreground" />
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent><p>{t('learner.team.myTeam.viewAsName', { name: member.name })}</p></TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    ) : <span className="text-muted-foreground">-</span>}
                                  </TableCell>
                                )}
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell className="text-center font-semibold">{member.totalPoints}</TableCell>
                                <TableCell className="text-center font-semibold">{member.rank}</TableCell>
                                <TableCell className="text-center font-semibold">{member.rankInDepartment}</TableCell>
                                <TableCell className="text-center font-semibold">{member.rankInOrg}</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                <TableCell><p className="text-sm">{member.designation}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Risk Score Tab */}
                  {showMyTeamRiskScore && (
                  <TabsContent value="risk-score" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:ps-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold ps-4">{t('learner.team.common.employee')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.membersInTeam')}</TableHead>
                              <TableHead className="font-semibold">{t('learner.team.common.department')}</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">{t('learner.team.myTeam.columns.hierarchy')}</TableHead>}
                              <TableHead className="font-semibold">{t('learner.team.myTeam.tabs.riskScore')}</TableHead>
                              <TableHead className="font-semibold">{t('learner.team.myTeam.columns.manager')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openTeamMember(member)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>{getRiskScoreBadge(member.riskLevel, member.riskScore)}</TableCell>
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Phishing Tab */}
                  {showMyTeamPhishing && (
                  <TabsContent value="phishing" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:ps-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold ps-4">{t('learner.team.common.employee')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.membersInTeam')}</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">{t('learner.team.myTeam.columns.hierarchy')}</TableHead>}
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.sent')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.opened')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.clicked')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.compromised')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.reported')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.trained')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.evaluated')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openTeamMember(member)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell className="text-center font-semibold">{member.phishing.sent}</TableCell>
                                <TableCell className="text-center">{member.phishing.opened} | {member.phishing.openedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.clicked} | {member.phishing.clickedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.compromised} | {member.phishing.compromisedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.reported} | {member.phishing.reportedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.trained} | {member.phishing.trainedRate}%</TableCell>
                                <TableCell className="text-center">{member.phishing.evaluated} | {member.phishing.evaluatedRate}%</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Training Tab */}
                  {showMyTeamTraining && (
                  <TabsContent value="training" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:ps-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold ps-4">{t('learner.team.common.employee')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.membersInTeam')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.assigned')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.completed')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.inProgress')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.trained')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.evaluated')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.expired')}</TableHead>
                              <TableHead className="font-semibold">{t('learner.team.common.department')}</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">{t('learner.team.myTeam.columns.hierarchy')}</TableHead>}
                              <TableHead className="font-semibold">{t('learner.team.myTeam.columns.manager')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openTeamMember(member)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell className="text-center font-semibold">{member.training.assigned}</TableCell>
                                <TableCell className="text-center">{member.training.completed} | {member.training.completedRate}%</TableCell>
                                <TableCell className="text-center">{member.training.inProgress} | {member.training.inProgressRate}%</TableCell>
                                <TableCell className="text-center">{member.training.trained} | {member.training.trainedRate}%</TableCell>
                                <TableCell className="text-center">{member.training.evaluated} | {member.training.evaluatedRate}%</TableCell>
                                <TableCell className="text-center">{member.training.expired} | {member.training.expiredRate}%</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}

                  {/* Announcement Tab */}
                  {showMyTeamAnnouncement && (
                  <TabsContent value="announcement" className="m-0">
                    <CardContent className="p-0 overflow-hidden">
                      <div className="overflow-x-auto w-full max-w-full">
                        <Table className="min-w-max [&_td:first-child]:ps-4">
                          <TableHeader>
                            <TableRow className="bg-muted/50">
                              <TableHead className="font-semibold ps-4">{t('learner.team.common.employee')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.myTeam.columns.membersInTeam')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.sent')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.opened')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.acknowledged')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.reported')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.trained')}</TableHead>
                              <TableHead className="font-semibold text-center">{t('learner.team.metrics.evaluated')}</TableHead>
                              <TableHead className="font-semibold">{t('learner.team.common.department')}</TableHead>
                              {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableHead className="font-semibold">{t('learner.team.myTeam.columns.hierarchy')}</TableHead>}
                              <TableHead className="font-semibold">{t('learner.team.myTeam.columns.manager')}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myTeamPaginatedMembers.map((member) => (
                              <TableRow key={member.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => openTeamMember(member)}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarImage src={member.avatar} alt={member.name} />
                                      <AvatarFallback>{member.name.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">{member.name}</p>
                                      <p className="text-sm text-muted-foreground">{member.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center font-semibold">{member.membersInTeam}</TableCell>
                                <TableCell className="text-center font-semibold">{member.announcement.sent}</TableCell>
                                <TableCell className="text-center">{member.announcement.opened} | {member.announcement.openedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.acknowledged} | {member.announcement.acknowledgedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.reported} | {member.announcement.reportedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.trained} | {member.announcement.trainedRate}%</TableCell>
                                <TableCell className="text-center">{member.announcement.evaluated} | {member.announcement.evaluatedRate}%</TableCell>
                                <TableCell><p className="font-medium">{member.department}</p></TableCell>
                                {(myTeamFilter === 'all' || myTeamFilter === 'indirect' || myTeamFilter === 'myteam') && <TableCell><HierarchyCell member={member} /></TableCell>}
                                <TableCell>
                                  <p className="font-medium">{member.managerName || myTeamCurrentManager.name}</p>
                                  {!member.managerName && (
                                    <p className="text-sm text-muted-foreground">{myTeamCurrentManager.email}</p>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </TabsContent>
                  )}
                </Card>
              </Tabs>

              {/* Pagination for My Team */}
              {myTeamActiveTabMembers.length > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {t('learner.team.common.showingMembers', {
                      from: (myTeamCurrentPage - 1) * myTeamItemsPerPage + 1,
                      to: Math.min(myTeamCurrentPage * myTeamItemsPerPage, myTeamActiveTabMembers.length),
                      count: myTeamActiveTabMembers.length,
                    })}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{t('learner.team.myTeam.rowsPerPage')}</span>
                      <Select
                        value={String(myTeamItemsPerPage)}
                        onValueChange={(value) => { setMyTeamItemsPerPage(Number(value)); setMyTeamCurrentPage(1); }}
                      >
                        <SelectTrigger className="w-[72px] h-8 text-foreground border-border" aria-label={t('learner.team.myTeam.rowsPerPage')}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[10, 25, 50, 100].map((size) => (
                            <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" aria-label={t('learner.team.common.previousPage')} onClick={() => setMyTeamCurrentPage((p) => Math.max(1, p - 1))} disabled={myTeamCurrentPage === 1}>
                        <ChevronLeft className="h-4 w-4 rtl-flip" />
                      </Button>
                      <span className="text-sm px-2">{t('learner.team.common.pageOf', { page: myTeamCurrentPage, pages: myTeamTotalPages })}</span>
                      <Button variant="outline" size="icon" aria-label={t('learner.team.common.nextPage')} onClick={() => setMyTeamCurrentPage((p) => Math.min(myTeamTotalPages, p + 1))} disabled={myTeamCurrentPage >= myTeamTotalPages}>
                        <ChevronRight className="h-4 w-4 rtl-flip" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
            </Tabs>
          </div>

          {/* Employee Gamification Modal — at the root, not inside the
              Dashboard tab, so a My Team row can open it too (audit T14). */}
          <Dialog open={selectedLeaderboardEmployee !== null} onOpenChange={(open) => {
            if (!open) {
              setSelectedLeaderboardEmployee(null);
              setActivityHistoryTab('badges');
              setActivityDurationFilter('all');
            }
          }}>
            <DialogContent className="max-w-[80vw] max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="pb-4">
                <DialogTitle>
                  {selectedLeaderboardEmployee && (
                    <div className="flex items-center justify-between">
                      {/* Employee Info */}
                      <div className="flex items-center gap-3">
                        <Avatar className="h-14 w-14">
                          <AvatarFallback className={`${getAvatarColor(selectedLeaderboardEmployee.name)} text-white text-lg font-semibold`}>
                            {getInitials(selectedLeaderboardEmployee.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col gap-1">
                          <p className="text-xl font-semibold">{selectedLeaderboardEmployee.name}</p>
                          <p className="text-sm text-muted-foreground font-normal">{selectedLeaderboardEmployee.email}</p>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div className="flex items-center gap-8">
                        {(() => {
                          const held = levelOf(selectedLeaderboardEmployee.totalPoints);
                          return (
                            <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg">
                              <span className="relative w-10 h-10 flex items-center justify-center">
                                <BadgeIcon name={held?.imageUrl} alt={held?.name ?? t('learner.team.common.level')} className="w-10 h-10" />
                              </span>
                              <div>
                                <p className="text-base font-semibold text-foreground">{held?.name ?? t('learner.team.levels.noLevelYet')}</p>
                                <p className="text-xs text-muted-foreground">{t('learner.team.dialogs.currentLevel')}</p>
                              </div>
                            </div>
                          );
                        })()}

                        <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-base font-semibold text-foreground">{selectedLeaderboardEmployee.totalPoints}</p>
                            <p className="text-xs text-muted-foreground">{t('learner.team.common.totalPoints')}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg">
                          <div className="flex -space-x-2">
                            {ownBadges(selectedLeaderboardEmployee).slice(0, 3).map((badge, i) => (
                              <div key={i} className="relative w-8 h-8 rounded-full ring-2 ring-white overflow-hidden bg-muted">
                                <BadgeIcon name={badge.icon} alt={badge.name} className="w-8 h-8" />
                              </div>
                            ))}
                          </div>
                          <div>
                            <p className="text-base font-semibold text-foreground">{selectedLeaderboardEmployee.badgeCount}</p>
                            <p className="text-xs text-muted-foreground">{t('learner.team.common.badgesEarned')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('learner.team.dialogs.employeeDescription')}
                </DialogDescription>
              </DialogHeader>

              {selectedLeaderboardEmployee && (
                <div className="flex-1 overflow-y-auto space-y-6 py-4">
                  {/* Badges Earned Section */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold">{t('learner.team.dialogs.badgesEarnedCount', { count: selectedLeaderboardEmployee.badgeCount })}</h3>
                    {selectedLeaderboardEmployee.badgeCount > 0 ? (
                      <div className="grid grid-cols-6 gap-4">
                        {(() => {
                          const history = employeeGamificationHistory[selectedLeaderboardEmployee.id] || [];
                          const badgeHistory = history.filter((h: { type: string }) => h.type === 'badge');
                          return ownBadges(selectedLeaderboardEmployee).map((badge, index) => {
                            const historyEntry = badgeHistory[index];
                            return (
                              <div key={index} className="p-4 bg-card rounded-lg border border-border hover:border-primary/30 hover:shadow-sm transition-all">
                                <div className="flex flex-col items-center gap-2">
                                  <div className="relative w-14 h-14">
                                    <BadgeIcon name={badge.icon} alt={badge.name} className="w-14 h-14" />
                                  </div>
                                  <p className="text-sm font-medium text-center" dir="auto">{badge.name}</p>
                                  {historyEntry && (
                                    <div className="w-full px-2 py-1.5 bg-muted/50 rounded-md">
                                      <p className="text-xs text-muted-foreground text-center line-clamp-2">{historyEntry.event}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-6">{t('learner.team.dialogs.noBadges')}</p>
                    )}
                  </div>

                  {/* Activity History Section with Tabs */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">{t('learner.team.dialogs.activityHistory')}</h3>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={activityHistoryTab === 'badges' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActivityHistoryTab('badges')}
                          className="h-7 px-3 text-xs"
                        >
                          {t('learner.team.common.badges')}
                        </Button>
                        <Button
                          variant={activityHistoryTab === 'levels' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActivityHistoryTab('levels')}
                          className="h-7 px-3 text-xs"
                        >
                          {t('learner.team.dialogs.levels')}
                        </Button>
                        <Button
                          variant={activityHistoryTab === 'points' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setActivityHistoryTab('points')}
                          className="h-7 px-3 text-xs"
                        >
                          {t('learner.team.common.points')}
                        </Button>
                      </div>
                    </div>

                    {/* Activity history — UnifiedDataTable (replaces bare <table>).
                        Columns vary by activityHistoryTab (badges/levels/points).
                        Data comes from local employeeGamificationHistory (preview
                        NC copy — no server pagination needed). */}
                    {(() => {
                      const history = employeeGamificationHistory[selectedLeaderboardEmployee.id] || [];
                      const filteredEntries = history.filter((e: { type: string }) =>
                        activityHistoryTab === 'badges' ? e.type === 'badge' :
                        activityHistoryTab === 'levels' ? e.type === 'level' :
                        e.type === 'points'
                      );

                      // The API sends each event's instant (ISO); shown in the reader's
                      // language and zone. Anything unparseable is shown as sent.
                      const dateTimeCell = ({ row }: { row: { original: LooseRow } }) => (
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatLearnerDate(row.original.dateTime, locale, DATE_TIME) || row.original.dateTime}
                        </span>
                      );

                      const badgeColumns = [
                        {
                          id: 'id',
                          accessorKey: 'id',
                          header: t('learner.team.activity.id'),
                          enableHiding: true,
                          size: 0,
                        },
                        {
                          id: 'dateTime',
                          accessorKey: 'dateTime',
                          header: t('learner.team.activity.dateTime'),
                          enableSorting: true,
                          cell: dateTimeCell,
                        },
                        {
                          id: 'name',
                          accessorKey: 'name',
                          header: t('learner.team.activity.badge'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            <div className="flex items-center gap-2">
                              {row.original.icon && (
                                <img src={row.original.icon} alt={row.original.name} className="w-8 h-8 object-contain" />
                              )}
                              <span className="text-sm font-medium">{row.original.name}</span>
                            </div>
                          ),
                        },
                        {
                          id: 'event',
                          accessorKey: 'event',
                          header: t('learner.team.activity.event'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            <Badge variant="outline" className="text-xs font-normal">
                              {row.original.event}
                            </Badge>
                          ),
                        },
                      ];

                      const levelColumns = [
                        {
                          id: 'id',
                          accessorKey: 'id',
                          header: t('learner.team.activity.id'),
                          enableHiding: true,
                          size: 0,
                        },
                        {
                          id: 'dateTime',
                          accessorKey: 'dateTime',
                          header: t('learner.team.activity.dateTime'),
                          enableSorting: true,
                          cell: dateTimeCell,
                        },
                        {
                          id: 'name',
                          accessorKey: 'name',
                          header: t('learner.team.common.level'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                </svg>
                              </div>
                              <span className="text-sm font-medium">{row.original.name}</span>
                            </div>
                          ),
                        },
                        {
                          id: 'pointsRequired',
                          accessorKey: 'pointsRequired',
                          header: t('learner.team.activity.pointsRequired'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            // Pass-through: BE supplies `pointsRequired` per
                            // level row. The old code hardcoded thresholds
                            // (200/400/800/1200/1600) that drifted from any
                            // real level config.
                            <span className="text-sm font-semibold">
                              {row.original.pointsRequired
                                ? t('learner.team.common.pts', { points: row.original.pointsRequired })
                                : '-'}
                            </span>
                          ),
                        },
                        {
                          id: 'previousLevel',
                          accessorKey: 'previousLevel',
                          header: t('learner.team.activity.previousLevel'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            // Pass-through: BE supplies `previousLevel` per row
                            // (the level-up's own record); '' on the first rung.
                            <Badge variant="outline" className="text-xs font-normal">
                              {row.original.previousLevel || '-'}
                            </Badge>
                          ),
                        },
                      ];

                      const pointsColumns = [
                        {
                          id: 'id',
                          accessorKey: 'id',
                          header: t('learner.team.activity.id'),
                          enableHiding: true,
                          size: 0,
                        },
                        {
                          id: 'dateTime',
                          accessorKey: 'dateTime',
                          header: t('learner.team.activity.dateTime'),
                          enableSorting: true,
                          cell: dateTimeCell,
                        },
                        {
                          id: 'amount',
                          accessorKey: 'amount',
                          header: t('learner.team.activity.pointsEarned'),
                          enableSorting: true,
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            <span className="text-sm font-semibold text-green-600">{row.original.amount}</span>
                          ),
                        },
                        {
                          id: 'name',
                          accessorKey: 'name',
                          header: t('learner.team.activity.activity'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            <Badge variant="outline" className="text-xs font-medium border-amber-200 bg-amber-50 text-amber-700">
                              {row.original.name}
                            </Badge>
                          ),
                        },
                        {
                          id: 'event',
                          accessorKey: 'event',
                          header: t('learner.team.activity.event'),
                          cell: ({ row }: { row: { original: LooseRow } }) => (
                            <span className="text-sm text-muted-foreground">{row.original.event}</span>
                          ),
                        },
                      ];

                      const activeColumns =
                        activityHistoryTab === 'badges' ? badgeColumns :
                        activityHistoryTab === 'levels' ? levelColumns :
                        pointsColumns;

                      const tablePrefix = `mgr-activity-history-${activityHistoryTab}`;
                      const noActivity =
                        activityHistoryTab === 'badges'
                          ? t('learner.team.activity.noBadgeActivity')
                          : activityHistoryTab === 'levels'
                            ? t('learner.team.activity.noLevelActivity')
                            : t('learner.team.activity.noPointsActivity');

                      return (
                        <UnifiedDataTable
                            columns={activeColumns}
                            data={filteredEntries}
                            localStoragePrefix={tablePrefix}
                            enableSearch={false}
                            enableDensityToggle={false}
                            columnDefaults={{ visibility: { id: false } }}
                            maxHeight="260px"
                            noResultsText={noActivity}
                            emptyState={{
                              mode: 'inline',
                              title: noActivity,
                              description: t('learner.team.activity.emptyDescription'),
                            }}
                          />
                      );
                    })()}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Team Hierarchy Sheet (Side Drawer)
              <HierarchyTree> is scoped to the VIEWED manager's real reportee
              sub-tree: rooted at `myTeamManagerProfile` and assembled from the
              flat `myTeamAllMembers` list (each linked via `reportsTo`). This
              shows only the selected manager's sub-tree — not the whole org.
              When no real manager context exists (admin-builder preview with no
              BE data — `myTeamManagerProfile.id` is empty), HierarchyTree falls
              back to its synthetic mock org for layout review only. */}
          <Sheet open={isHierarchyModalOpen} onOpenChange={setIsHierarchyModalOpen}>
            <SheetContent side="end" className="w-[60vw] sm:max-w-[60vw] p-0">
              <SheetHeader className="px-6 py-4 border-b">
                <SheetTitle>{t('learner.team.hierarchy.sheetTitle')}</SheetTitle>
              </SheetHeader>
              <div className="h-[calc(100vh-80px)]">
                {myTeamManagerProfile.id ? (
                  <HierarchyTree
                    rootMember={{
                      id: myTeamManagerProfile.id,
                      name: myTeamManagerProfile.name,
                      email: myTeamManagerProfile.email,
                      avatar: myTeamManagerProfile.avatar,
                      department: myTeamManagerProfile.department,
                      designation: myTeamManagerProfile.designation,
                    }}
                    members={myTeamAllMembers.map((m) => ({
                      id: m.id,
                      name: m.name,
                      email: m.email,
                      avatar: m.avatar,
                      department: m.department,
                      designation: m.designation,
                      isManager: m.isManager,
                      reportsTo: m.reportsTo,
                    }))}
                  />
                ) : previewMode ? (
                  // Admin-builder preview with no real manager context: show the
                  // synthetic mock org for layout review only. The mock never
                  // reaches a live employee (gated by `previewMode`).
                  <HierarchyTree />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('learner.team.hierarchy.unavailable')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('learner.team.hierarchy.reportCounts', {
                        direct: dashboardData?.directReportCount ?? 0,
                        indirect: dashboardData?.indirectReportCount ?? 0,
                      })}
                    </p>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

          {/* Employee List Modal */}
          <Dialog open={isEmployeeListModalOpen} onOpenChange={(open) => {
            setIsEmployeeListModalOpen(open);
            if (!open) setCurrentPage(1);
          }}>
            <DialogContent className="sm:max-w-4xl w-[95vw] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {t('learner.team.dialogs.categoryTitle', {
                    category:
                      employeeListCategory === 'direct'
                        ? t('learner.team.common.directReports')
                        : employeeListCategory === 'indirect'
                          ? t('learner.team.common.indirectReports')
                          : employeeListCategory === 'managers'
                            ? t('learner.team.dialogs.managers')
                            : t('learner.team.dashboard.reportees.all'),
                    count: getEmployeesForCategory().length,
                  })}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t('learner.team.dialogs.categoryDescription')}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                {(() => {
                  const employees = getEmployeesForCategory();
                  const totalPages = Math.ceil(employees.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage;
                  const paginatedEmployees = employees.slice(startIndex, startIndex + itemsPerPage);

                  return (
                    <>
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('learner.team.common.employee')}</TableHead>
                            {gamificationOn && (
                              <>
                                <TableHead>{t('learner.team.common.points')}</TableHead>
                                <TableHead>{t('learner.team.common.level')}</TableHead>
                                <TableHead>{t('learner.team.common.badges')}</TableHead>
                              </>
                            )}
                            <TableHead>{t('learner.team.dialogs.risk')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedEmployees.map((employee) => {
                            const levelInfo = levelOf(employee.totalPoints);
                            return (
                              <TableRow 
                                key={employee.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => openTeamMember(employee)}
                              >
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                      <AvatarFallback className={`${getAvatarColor(employee.name)} text-white text-sm font-semibold`}>
                                        {getInitials(employee.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium text-sm">{employee.name}</p>
                                      <p className="text-xs text-muted-foreground">{employee.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                {gamificationOn && (
                                <>
                                <TableCell className="font-medium">{t('learner.team.common.number', { value: employee.totalPoints ?? 0 })}</TableCell>
                                <TableCell>
                                  {levelInfo ? (
                                    <div className="flex items-center gap-2">
                                      <span className="relative w-8 h-8 flex items-center justify-center">
                                        <BadgeIcon name={levelInfo.imageUrl} alt={levelInfo.name} className="w-8 h-8" />
                                      </span>
                                      <span className="text-sm font-medium">{levelInfo.name}</span>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">{t('learner.team.levels.noLevelYet')}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="flex -space-x-1">
                                      {badgeImages.slice(0, Math.min(employee.badgeCount, 3)).map((src, i) => (
                                        <img 
                                          key={i} 
                                          src={src} 
                                          alt={t('learner.team.common.badgeAlt', { n: i + 1 })} 
                                          className="w-6 h-6 rounded-full ring-1 ring-white object-cover" 
                                        />
                                      ))}
                                    </div>
                                    <span className="text-sm text-muted-foreground">
                                      {employee.badgeCount}
                                    </span>
                                  </div>
                                </TableCell>
                                </>
                                )}
                                <TableCell>
                                  <Badge className={getRiskLevelColor(employee.riskLevel)}>
                                    {riskLevelText(t, employee.riskLevel)}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <p className="text-sm text-muted-foreground">
                            {t('learner.team.common.showingMembers', {
                              from: startIndex + 1,
                              to: Math.min(startIndex + itemsPerPage, employees.length),
                              count: employees.length,
                            })}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1}
                            >
                              <ChevronLeft className="h-4 w-4 rtl-flip" />
                              {t('learner.team.common.previous')}
                            </Button>
                            <div className="flex items-center gap-1">
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <Button
                                  key={page}
                                  variant={currentPage === page ? 'default' : 'outline'}
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </Button>
                              ))}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={currentPage === totalPages}
                            >
                              {t('learner.team.common.next')}
                              <ChevronRight className="h-4 w-4 rtl-flip" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </DialogContent>
          </Dialog>

          {/* Weekly Recap Modal — LAST WEEK: the last completed week, Monday
              00:00 to Monday 00:00 in the zone the API names (audit 2026-09-25
              batch B; locale audit 2026-09-26). Once per week, dismissible
              (gated by the localStorage key
              hf-portal:weekly-recap-dismissed:<the recap's weekStart>;
              lib/portal/weekly-recap-dismissal.ts). It reads ONLY what the
              API's recap sends (WeeklyRecapResponseDto): net points, badges
              earned, the top performer, and simulated phishing clicks and
              reports. */}
          <Dialog
            open={showWeeklyRecap}
            onOpenChange={(open) => {
              if (!open) handleDismissWeeklyRecap();
            }}
          >
            <DialogContent className="w-[70vw] !max-w-[70vw] h-[70vh] p-0 overflow-hidden border border-border bg-background rounded-xl shadow-2xl [&>button]:hidden">
              <DialogTitle className="sr-only">{t('learner.team.recap.title')}</DialogTitle>
              <DialogDescription className="sr-only">
                {t('learner.team.recap.description')}
              </DialogDescription>
              {weeklyRecap ? (
              <div className="relative overflow-hidden h-full flex flex-col">
                {/* Confetti when the team gained points last week */}
                {currentRecapSlide === 'gamification' && weeklyRecap.ptsEarned > 0 && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
                    {[...Array(50)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute animate-confetti"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: '-20px',
                          animationDelay: `${Math.random() * 3}s`,
                          animationDuration: `${3 + Math.random() * 2}s`,
                        }}
                      >
                        <div
                          className="w-3 h-3 rotate-45"
                          style={{
                            backgroundColor: ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#06b6d4'][Math.floor(Math.random() * 6)],
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Sad face rain when someone clicked a simulated phish */}
                {currentRecapSlide === 'phishing' && weeklyRecap.phishingClicks > 0 && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
                    {[...Array(20)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute animate-fall text-2xl opacity-70"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: '-40px',
                          animationDelay: `${Math.random() * 4}s`,
                          animationDuration: `${4 + Math.random() * 3}s`,
                        }}
                      >
                        😢
                      </div>
                    ))}
                  </div>
                )}

                {/* Close Button — dismisses the recap for its own week */}
                <button
                  onClick={handleDismissWeeklyRecap}
                  aria-label={t('learner.team.recap.close')}
                  className="absolute top-4 end-4 z-20 p-2 rounded-full bg-background/80 hover:bg-background shadow-lg transition-all hover:scale-110"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Header */}
                <div className="bg-muted/20 p-5 pb-4 border-b border-border">
                  <h2 className="text-xl font-bold text-foreground">{t('learner.team.recap.title')}</h2>
                  <p className="text-xs text-muted-foreground">
                    {recapPeriod
                      ? t('learner.team.recap.periodMembers', { period: recapPeriod, count: allTeamMembers.length })
                      : t('learner.team.recap.members', { count: allTeamMembers.length })}
                  </p>
                </div>

                {/* Carousel Content */}
                <div className="p-6 pt-4 flex-1 overflow-y-auto">
                  {/* Slide 0: Gamification */}
                  {currentRecapSlide === 'gamification' && (
                    <div className="animate-fadeIn">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">{t('learner.team.recap.gamification')}</Badge>
                          </div>
                          <p className="text-lg font-semibold">{t('learner.team.recap.teamPoints')}</p>
                          <p className="text-xs text-muted-foreground">{t('learner.team.recap.netOfRevoked')}</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="page-heading text-foreground" data-testid="recap-points">
                            {signedPoints(weeklyRecap.ptsEarned)}
                          </span>
                          <span className="text-sm text-muted-foreground">{t('learner.team.recap.pointsNoun', { count: weeklyRecap.ptsEarned })}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-xs text-muted-foreground font-medium mb-1">{t('learner.team.recap.badgesEarned')}</p>
                          <p className="text-xl font-bold text-foreground" data-testid="recap-badges">
                            {weeklyRecap.badgesEarned}
                          </p>
                        </div>
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <p className="text-xs text-muted-foreground font-medium mb-1">{t('learner.team.recap.topPerformer')}</p>
                          <div data-testid="recap-top-performer">
                            {weeklyRecap.topPerformerName ? (
                              <p className="text-xl font-bold text-foreground">
                                <UserText>{weeklyRecap.topPerformerName}</UserText>{' '}
                                <span className="text-sm font-normal text-muted-foreground">
                                  {t('learner.team.common.pts', { points: signedPoints(weeklyRecap.topPerformerPoints) })}
                                </span>
                              </p>
                            ) : (
                              <p className="text-sm text-muted-foreground">{t('learner.team.recap.nobodyEarned')}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Slide 1: Simulated phishing */}
                  {currentRecapSlide === 'phishing' && (
                    <div className="animate-fadeIn">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">{t('learner.team.myTeam.tabs.phishing')}</Badge>
                          </div>
                          <p className="text-lg font-semibold">{t('learner.team.recap.simulatedPhishing')}</p>
                          <p className="text-xs text-muted-foreground">{t('learner.team.recap.simulatedPhishingHint')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <div className="flex items-center gap-2 mb-1">
                            <MousePointer className="w-3.5 h-3.5 text-orange-600" />
                            <p className="text-xs text-muted-foreground font-medium">{t('learner.team.metrics.clicked')}</p>
                          </div>
                          <p className="text-xl font-bold text-red-600" data-testid="recap-clicks">
                            {weeklyRecap.phishingClicks}
                          </p>
                        </div>
                        <div className="bg-muted/10 rounded-lg p-4 border">
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-3.5 h-3.5 text-green-600" />
                            <p className="text-xs text-muted-foreground font-medium">{t('learner.team.metrics.reported')}</p>
                          </div>
                          <p className="text-xl font-bold text-green-600" data-testid="recap-reports">
                            {weeklyRecap.phishingReports}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="px-6 py-3 border-t border-border bg-muted/10">
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecapSlide((prev) => (prev - 1 + totalRecapSlides) % totalRecapSlides)}
                      className="gap-1"
                    >
                      <ChevronLeft className="h-4 w-4 rtl-flip" />
                      {t('learner.team.common.previous')}
                    </Button>

                    {/* Carousel Dots */}
                    <div className="flex items-center gap-2">
                      {Array.from({ length: totalRecapSlides }, (_, idx) => (
                        <button
                          key={idx}
                          aria-label={t('learner.team.recap.goToSlide', { n: idx + 1 })}
                          onClick={() => setRecapSlide(idx)}
                          className={`h-2 rounded-full transition-all ${
                            recapSlide % totalRecapSlides === idx ? 'bg-primary w-8' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2'
                          }`}
                        />
                      ))}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecapSlide((prev) => (prev + 1) % totalRecapSlides)}
                      className="gap-1"
                    >
                      {t('learner.team.common.next')}
                      <ChevronRight className="h-4 w-4 rtl-flip" />
                    </Button>
                  </div>
                </div>
              </div>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t('learner.team.recap.noData')}
                </div>
              )}
            </DialogContent>
          </Dialog>
    </TooltipProvider>
  );
}
